import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  Menu,
  MenuItem,
  ButtonGroup,
  ListItemText,
  CircularProgress,
  LinearProgress,
  Alert,
  Dialog,
  IconButton,
  Paper,
  Badge,
  Checkbox,
  Divider,
  Tooltip,
  Stack,
  Skeleton,
  alpha,
  Portal,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  Search,
  Visibility,
  ViewModule,
  ViewList,
  Lock,
  InfoOutlined as InfoIcon,
  Archive,
  CloudSync,
  ArrowDropDown,
  PlaylistAdd,
  PlaylistRemove,
  WarningAmber,
  Download,
} from '@mui/icons-material';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiService, CatalogProductStatus, CatalogSort, Product, SyncSelectionCapError } from '../services/api';
import { brandManagementApi } from '../services/brandManagementApi';
import { useBrandRegistry } from '../hooks/useBrandRegistry';
import { usePriceVariesByLocation } from '../hooks/usePriceVariesByLocation';
import { TradeInCreditLine } from '../components/productDetails/TradeInCreditLine';
import { ImageGallery } from '../components/ImageGallery';
import { ProductPricingData } from '../components/ProductPricingBadge';
import { ComboRebateOffersNote } from '../components/ComboRebateOffersNote';
import { ProductDetailsDialog } from '../components/productDetails/ProductDetailsDialog';
import { CompareSelectionButton, buildCompareUrl, canCompareSelection } from '../components/compare/CompareSelectionButton';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useActingStoreAdmin } from '../hooks/useActingStoreAdmin';
import { useVendorStatus } from '../hooks/useVendorStatus';
import { useIsMobile } from '../hooks/useIsMobile';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import {
  PermalinkNotFoundError,
  usePermalinkNavigation,
  useProductPermalink,
} from '../hooks/useProductPermalink';
import { PermalinkNotFound } from '../components/productPermalink/PermalinkNotFound';
import { PermalinkLoadFailed } from '../components/productPermalink/PermalinkLoadFailed';
import { useStoreIntegrations } from './storeHome/useStoreHomeData';
import { describeNextScheduledSync, pickScheduleIntegration } from '../utils/syncSchedule';
import { resolveVendorCatalogScope } from '../utils/vendorCatalogAccess';
import { stripHtml } from '../utils/text';
import { getEffectiveMapForProduct, rebateWindowCaption } from '../utils/mapPricing';
import { withOwnProductPrice } from '../utils/ownProductPrice';
import { useLocale } from '../contexts/LocaleContext';
import { getDealerUrl } from '../utils/dealerUrl';
import { variantDisplayLabel } from '../utils/productVariants';
import { catalogIdentityLine } from '../utils/catalogIdentity';
import { getVendorInfo, normalizeWikimediaThumbUrl } from '../utils/vendorLogos';
import {
  brandFamilyKey,
  brandIdentityKey,
  brandsMatchIdentity,
  mergeBrandLabels,
  resolveBrandDisplayName,
} from '../utils/brandIdentity';
import ProductTypeTabs, { ProductTypeTabItem } from '../components/productBrowser/ProductTypeTabs';
import {
  PRODUCT_CARD_GRID_SX,
  ProductCardSkeletonGrid,
  ProductListSkeleton,
} from '../components/productBrowser/ProductBrowserSkeletons';
import FilterPill from '../components/productBrowser/FilterPill';
import PricingOnlyPlaceholdersCard, { PricingOnlyPlaceholdersToggle } from '../components/placeholders/PricingOnlyPlaceholdersCard';
import StoreProductUploadCard, { StoreProductUploadToggle } from '../components/placeholders/StoreProductUploadCard';
import ProductSubmissionsCard, { ProductSubmissionsToggle } from '../components/placeholders/ProductSubmissionsCard';
import AddFilterMenu from '../components/productBrowser/AddFilterMenu';
import { DateField, PageHeader, StatusChip } from '../components/ui';
import { PendingReviewChip } from '../components/PendingReviewChip';
import ViewsMenu from '../components/productBrowser/ViewsMenu';
import PaginationBar from '../components/productBrowser/PaginationBar';
import { BrandMark } from '../components/productBrowser/BrandMark';
import {
  ProductFilterFieldDef,
  ProductFilterMark,
  ProductFilterOption,
  ProductFilterValue,
  ProductPillKey,
  ADDED_FILTER_PRESETS,
  addedFilterToSince,
  customAddedFilterValue,
  isAddedFilterValue,
  localIsoDay,
  applyMatchFilter,
  isMatchFilterValue,
  MatchFilterValue,
  applyLifecycleFilter,
  attachLifecycle,
  filterPreReleaseRows,
  isLifecycleFilterValue,
  lifecycleStageOf,
  LifecycleFilterValue,
} from '../utils/productBrowserFilterState';
import ProductLifecycleChip from '../components/productLifecycle/ProductLifecycleChip';
import SyncCapNotice from '../components/productBrowser/SyncCapNotice';
import VariantGroupSelectAll from '../components/productBrowser/VariantGroupSelectAll';
import { ProductBrowserSavedFilters, ProductBrowserSavedView, sameSavedFilters, useSavedViews } from '../utils/useSavedViews';
import {
  CameraWithPricing,
  toListingPricingData,
  getImageUrl,
  toAbsoluteImageUrl,
  formatDate,
  formatRelativeDate,
  pluralize,
  lastPriceChangeAt,
  daysUntilDate,
  useProductPriceFormatters,
} from '../utils/productDetailHelpers';
import {
  filterStoreCatalogRows,
  isDiscontinuedInSync,
  isPriceUnverifiedInSync,
  storeRequestCatalogStatus,
} from '../utils/storeCatalogVisibility';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { isProductInPreorder } from '../utils/productPreorder';
import {
  buildSyncCountMap,
  countDiscontinuedInSyncRows,
  resolveStoreBrowserDataSource,
  gridRequestReadsUniverse,
  resolveSyncCountDenominator,
  type StoreSyncStatusFilter,
  filterStoreSyncRows,
} from '../utils/storeBrowserDataSource';
import { canKeepPreviousProductsPage, productsPlaceholderShapeFromKey } from '../utils/productBrowserPlaceholder';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';

/**
 * The store's whole brand-scoped dataset, one query (MOT-628). The facet counts
 * and brand options read it as the universe, and a client-paginated grid with no
 * other filter reads the same query instead of fetching the same URL again. It
 * is the heaviest request this page makes (up to 10,000 slim rows), so it is
 * never retried by react-query (a slow answer invited retries that stacked up on
 * one `api` instance) and stays fresh for ten minutes. `signal` is kept: when
 * the universe is no longer needed the request in flight is aborted.
 */
const STORE_UNIVERSE_FRESH_MS = 10 * 60 * 1000;
const storeUniverseQuery = (catalogStatus: CatalogProductStatus, integrationId: string | null) =>
  queryOptions({
    queryKey: ['storeBrandUniverse', catalogStatus, integrationId] as const,
    queryFn: ({ signal }) => apiService.getStoreProducts(10000, 0, { catalogStatus, integrationId, signal }),
    retry: 0,
    staleTime: STORE_UNIVERSE_FRESH_MS,
    gcTime: STORE_UNIVERSE_FRESH_MS,
  });

function parseCatalogStatusFromSearchParams(sp: URLSearchParams): CatalogProductStatus {
  const raw = (sp.get('catalogStatus') || '').toLowerCase();
  if (raw === 'archived' || raw === 'all' || raw === 'active') return raw;
  const legacy = (sp.get('showArchived') || '').toLowerCase();
  if (legacy === '1' || legacy === 'true' || legacy === 'yes') return 'all';
  return 'active';
}


const firstSpecLine = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const line = value.split('\n')[0]?.trim();
  return line || undefined;
};

const toPricingLookupKey = (model: string, productId?: string): string =>
  `${productId || ''}::${model}`;

/** Product metadata chips (brand, MAP, stock, sync) — filled and outlined share one height. */
const META_CHIP_SX = {
  fontSize: '0.75rem',
  boxSizing: 'border-box' as const,
  '& .MuiChip-label': { px: 1 },
} as const;

/** Soft green rebate chip — readable dark text (no red-on-green clash). */
const rebateBannerSx = {
  bgcolor: tokens.signal.positive.tint,
  border: '1px solid',
  borderColor: tokens.signal.positive.border,
} as const;

const rebateTextSx = { color: tokens.signal.positive.ink } as const;
const rebateEndDateSx = { color: 'text.secondary', fontWeight: 700 } as const;

/**
 * "Varies by location" — fetched lazily and batched across every visible
 * card (see `usePriceVariesByLocation`), not one request per row. Renders
 * nothing while unknown, on error, or when the price is the same everywhere.
 */
const VariesByLocationChip: React.FC<{ productId: string; enabled: boolean; label: string }> = ({
  productId,
  enabled,
  label,
}) => {
  const varies = usePriceVariesByLocation(productId, enabled);
  if (!varies) return null;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: 0.875, borderRadius: '4px', bgcolor: 'grey.100', color: 'text.secondary', fontSize: '0.6875rem', fontWeight: 600 }}>
      {label}
    </Box>
  );
};

export const CameraBrowserPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation() as { t: (key: string) => string };
  const tx = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { getEffectiveUserType, getEffectiveRole, isImpersonating, impersonatedRole, impersonatedBrand, impersonatedOrg } = useImpersonation();
  const { canManage: canManageActingStore } = useActingStoreAdmin();
  const { formatCurrency, formatNumber, currency: regionCurrency } = useLocale();
  const { organization } = useVendorStatus();
  const { enabled: syncPriceVerificationAlertsEnabled } = useOrgFeatureFlag('sync_price_verification_alerts');
  // Distributor carry (MOT-159): with the flag on, a distributor browses only its approved carry list.
  const { enabled: distributorCarryEnabled } = useOrgFeatureFlag('distributor_carry');
  // Direct product links (MOT-833): the open product lives in the address, /app/products/:productId.
  const { enabled: permalinksOn, isLoading: permalinkFlagLoading } = useOrgFeatureFlag('product_permalinks');
  const { productId: routeProductId = '' } = useParams<{ productId: string }>();
  const theme = useTheme();
  const isNarrow = useIsMobile();
  const effectiveUserType = getEffectiveUserType(userProfile);
  const effectiveRole = getEffectiveRole(userProfile);
  const isVendor = effectiveUserType === 'vendor_supplier';
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  // Gate admin-only UI by the EFFECTIVE role so an admin impersonating a brand or
  // store sees exactly what that account sees. `isAdmin` stays the real-user flag.
  const isPlatformAdmin = effectiveRole === 'admin';
  
  // Fetch store organization data to get brandsSold
  // For store users: use /organizations/me. For admin impersonating store: fetch full org by ID (impersonatedOrg may not include brandsSold).
  const { data: storeOrgData } = useQuery({
    queryKey: ['store-organization'],
    queryFn: async () => {
      const { makeApiCall, getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/organizations/me', { headers });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to fetch organization');
      }
      return response.json();
    },
    enabled: effectiveRole === 'store' && !isImpersonating,
  });

  // Fetch the org whenever View-as names one. The session can still say
  // vendor; the catalog follows the record, and a store uses brandsSold.
  const { data: impersonatedOrgFull } = useQuery({
    queryKey: ['impersonated-store-organization', impersonatedOrg?.id],
    queryFn: async () => {
      const { makeApiCall, getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/organizations/${impersonatedOrg!.id}`, { headers });
      if (!response.ok) return null;
      const json = await response.json();
      return json.data;
    },
    enabled: !!(isImpersonating && impersonatedOrg?.id),
  });

  // Get the store's selected brands (brandsSold array - brand IDs)
  // Prefer impersonatedOrgFull when impersonating (has brandsSold); else storeOrgData for store users; else impersonatedOrg as fallback
  const storeOrg = impersonatedOrgFull || storeOrgData?.data || impersonatedOrg;
  const storeBrandIds = useMemo(() => {
    const brands = storeOrg?.brandsSold || [];
    const toBrandToken = (
      value:
        | string
        | {
            brandId?: string;
            id?: string;
            name?: string;
            slug?: string;
            brand?: string;
            brandName?: string;
            vendorId?: string;
            vendorName?: string;
            brandRef?: { id?: string; name?: string; slug?: string };
            brandData?: { id?: string; name?: string; slug?: string };
          }
    ): string => {
      if (typeof value === 'string') return value;
      return (
        value.brandId ||
        value.id ||
        value.slug ||
        value.name ||
        value.brand ||
        value.brandName ||
        value.vendorId ||
        value.vendorName ||
        value.brandRef?.id ||
        value.brandRef?.slug ||
        value.brandRef?.name ||
        value.brandData?.id ||
        value.brandData?.slug ||
        value.brandData?.name ||
        ''
      );
    };
    const toKey = (value: string) => value.toLowerCase().trim();
    const seen = new Set<string>();
    return brands
      .map(
        (
          b:
            | string
            | {
                brandId?: string;
                id?: string;
                name?: string;
                slug?: string;
                brand?: string;
                brandName?: string;
                vendorId?: string;
                vendorName?: string;
                brandRef?: { id?: string; name?: string; slug?: string };
                brandData?: { id?: string; name?: string; slug?: string };
              }
        ) =>
          toBrandToken(b)
      )
      .filter((value: string) => {
        if (!value) return false;
        const key = toKey(value);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }) as string[];
  }, [storeOrg]);
  // A store stays on the brands it selected. The company name is not a brand,
  // even when the session was labeled as a brand account.
  const viewedOrgType = String(
    impersonatedOrgFull?.type || storeOrgData?.data?.type || impersonatedOrg?.type || '',
  );
  const orgIsStore = viewedOrgType === 'store' || viewedOrgType === 'retail' || viewedOrgType === 'camera_store';
  const isStoreScoped = effectiveRole === 'store' || orgIsStore;
  const listGridColumns = isNarrow
    ? isStoreScoped
      ? '28px 40px minmax(0,1fr)'
      : '40px minmax(0,1fr)'
    : isStoreScoped
      ? '36px 44px minmax(0,3fr) minmax(0,1.1fr) minmax(0,1fr) minmax(0,1.15fr) minmax(0,0.95fr)'
      : '44px minmax(0,3fr) minmax(0,1.1fr) minmax(0,1fr) minmax(0,1.15fr) minmax(0,0.95fr)';
  const isStoreWithBrands = isStoreScoped && storeBrandIds.length > 0;
  const showStoreOverridesPanel = isStoreScoped || isPlatformAdmin;
  const canManageStoreOverrides = isStoreScoped;
  // The AI copy routes (flag `store_ai_copy`) write for an owner or admin of
  // the acting store, or a platform admin viewing as it. Everyone else sees
  // the block read-only.
  const canManageStoreAiCopy = isStoreScoped && canManageActingStore;
  
  // Demo mode: admin impersonating vendor without a specific brand/org
  const isDemoMode =
    isImpersonating &&
    impersonatedRole === 'vendor' &&
    !impersonatedBrand?.id &&
    !impersonatedOrg?.id &&
    !organization?.id;
  
  // Get vendor's brand name for filtering
  const vendorBrandName = isDemoMode 
    ? 'ProMaster' // Use ProMaster as demo brand since we have data for it
    : (impersonatedBrand?.name || organization?.vendorName || organization?.name || '');
  const vendorCatalogScope = useMemo(
    () =>
      resolveVendorCatalogScope({
        vendorType: organization?.vendorType,
        ownBrandName: vendorBrandName,
        ownBrandId: impersonatedBrand?.id || organization?.vendorInfo?.brandId || organization?.brandId,
        catalogBrandIds: organization?.catalogBrandIds,
        requireApprovedCarry: distributorCarryEnabled,
      }),
    [
      organization?.vendorType,
      organization?.vendorInfo?.brandId,
      organization?.brandId,
      organization?.catalogBrandIds,
      vendorBrandName,
      impersonatedBrand?.id,
      distributorCarryEnabled,
    ],
  );
  const vendorCanBrowseOtherBrands = isVendor && !isStoreScoped && vendorCatalogScope.canBrowseOtherBrands;

  const parsePage = (): number => {
    const raw = Number(searchParams.get('page') || '1');
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
  };
  const parseLimit = (): number => {
    const raw = Number(searchParams.get('limit') || '25');
    return [25, 50, 75, 100].includes(raw) ? raw : 25;
  };
  const parseProductType = (): 'all' | 'camera' | 'lens' | 'accessory' => {
    const raw = (searchParams.get('productType') || 'all').toLowerCase();
    return raw === 'camera' || raw === 'lens' || raw === 'accessory' ? raw : 'all';
  };
  const parseStoreBrands = (): string[] => {
    const raw = searchParams.get('brands') || '';
    return raw
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  };

  const [page, setPage] = useState(parsePage);
  const [debouncedPage, setDebouncedPage] = useState(parsePage);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('search') || '');
  const pageDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const openedDeepLinkRef = useRef<string | null>(null);

  // Debounce page changes to prevent rapid API calls
  useEffect(() => {
    if (pageDebounceRef.current) {
      clearTimeout(pageDebounceRef.current);
    }
    pageDebounceRef.current = setTimeout(() => {
      setDebouncedPage(page);
    }, 300);
    return () => {
      if (pageDebounceRef.current) {
        clearTimeout(pageDebounceRef.current);
      }
    };
  }, [page]);

  // Debounce search to prevent API calls on every keystroke
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery]);
  /** Search button / Enter: run the search now instead of waiting for the debounce. */
  const runSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setDebouncedSearch(searchQuery);
  };
  // For vendors, default to their brand; for others, allow any brand
  const [brandFilter, setBrandFilter] = useState(() => searchParams.get('brand') || '');
  const [storeBrandFilters, setStoreBrandFilters] = useState<string[]>(parseStoreBrands);
  const [categoryFilter, setCategoryFilter] = useState(() => searchParams.get('category') || '');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [exportingCsv, setExportingCsv] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<CameraWithPricing | null>(null);
  const [variantSwitchLoadingSku, setVariantSwitchLoadingSku] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const selectedCameraRef = useRef<CameraWithPricing | null>(null);
  selectedCameraRef.current = selectedCamera;
  const permalinkNav = usePermalinkNavigation('/app/products', permalinksOn);
  const permalink = useProductPermalink<CameraWithPricing>({
    enabled: permalinksOn,
    scope: 'catalog',
    routeId: routeProductId,
    fetchById: async (id) => {
      const product = await apiService.getCameraById(id);
      if (!product?.id) throw new PermalinkNotFoundError();
      return product as CameraWithPricing;
    },
  });
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [productTypeFilter, setProductTypeFilter] = useState<'all' | 'camera' | 'lens' | 'accessory'>(parseProductType);
  const [catalogStatus, setCatalogStatus] = useState<CatalogProductStatus>(() =>
    parseCatalogStatusFromSearchParams(searchParams)
  );
  const [limit, setLimit] = useState(parseLimit);
  /** List order. Name only: the catalog indexes serve it without a collection scan. */
  const [sort, setSort] = useState<CatalogSort>('name_asc');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [allFilteredSelected, setAllFilteredSelected] = useState(false);
  const [selectAllFilteredLoading, setSelectAllFilteredLoading] = useState(false);
  const [syncStatusFilter, setSyncStatusFilter] = useState<StoreSyncStatusFilter>('all');
  /** Store-only drill-down into the discontinued rows the store is still syncing. */
  const [discontinuedInSyncOnly, setDiscontinuedInSyncOnly] = useState(false);
  const { showToast } = useToast();
  /** "Added" filter value (`''` off); see `addedFilterToSince`. Flag `product_browser_added_filter`. */
  const [addedFilter, setAddedFilter] = useState('');
  /** "Matched to my store" filter (flag `product_browser_match_filter`): crosswalk match x sync selection. */
  const [matchFilter, setMatchFilter] = useState<MatchFilterValue>('all');
  /** Lifecycle filter (flag `preorder_lifecycle`): announced, pre-order or released products. */
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleFilterValue>('all');
  /** Set when the plan cap refuses an add to sync, so the notice can say why. */
  const [syncCapError, setSyncCapError] = useState<SyncSelectionCapError | null>(null);
  const productType = productTypeFilter === 'all' ? undefined : productTypeFilter;

  // Sync local state from URL (supports refresh, shared links, back/forward).
  useEffect(() => {
    const nextPage = parsePage();
    const nextSearch = searchParams.get('search') || '';
    const nextCategory = searchParams.get('category') || '';
    const nextProductType = parseProductType();
    const nextLimit = parseLimit();
    const nextCatalogStatus = parseCatalogStatusFromSearchParams(searchParams);
    const nextBrand = searchParams.get('brand') || '';
    const nextBrands = parseStoreBrands();

    if (page !== nextPage) setPage(nextPage);
    if (searchQuery !== nextSearch) setSearchQuery(nextSearch);
    if (categoryFilter !== nextCategory) setCategoryFilter(nextCategory);
    if (productTypeFilter !== nextProductType) setProductTypeFilter(nextProductType);
    if (limit !== nextLimit) setLimit(nextLimit);
    if (catalogStatus !== nextCatalogStatus) setCatalogStatus(nextCatalogStatus);

    if (isStoreScoped) {
      if (storeBrandFilters.join(',') !== nextBrands.join(',')) {
        setStoreBrandFilters(nextBrands);
      }
    } else if (brandFilter !== nextBrand && !brandsMatchIdentity(brandFilter, nextBrand)) {
      setBrandFilter(nextBrand);
    }
  }, [searchParams, isStoreScoped]);

  // Sync URL from current filters/page so links are shareable and restorable.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value?: string) => {
      if (value && value.trim().length > 0) next.set(key, value);
      else next.delete(key);
    };

    setOrDelete('search', searchQuery);
    setOrDelete('category', categoryFilter);
    setOrDelete('productType', productTypeFilter === 'all' ? '' : productTypeFilter);
    setOrDelete('limit', limit !== 25 ? String(limit) : '');
    setOrDelete('page', page > 1 ? String(page) : '');
    setOrDelete('catalogStatus', catalogStatus !== 'active' ? catalogStatus : '');
    next.delete('showArchived');

    if (isStoreScoped) {
      setOrDelete('brands', storeBrandFilters.join(','));
      next.delete('brand');
    } else {
      setOrDelete('brand', brandFilter);
      next.delete('brands');
    }

    const currentQuery = searchParams.toString();
    const nextQuery = next.toString();
    if (currentQuery !== nextQuery) {
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    searchQuery,
    categoryFilter,
    productTypeFilter,
    limit,
    page,
    catalogStatus,
    isStoreScoped,
    storeBrandFilters,
    brandFilter,
  ]);
  
  // Manufacturers stay locked to their brand. A store is never locked to its
  // company name; it uses the brands it selected. Distributors can clear the
  // picker to browse the rest of the catalog.
  useEffect(() => {
    if (isImpersonating && impersonatedOrg?.id && impersonatedOrgFull === undefined) return;
    if (isStoreScoped) return;
    if (isVendor && vendorBrandName && !brandFilter && !vendorCanBrowseOtherBrands) {
      setBrandFilter(vendorBrandName);
    }
  }, [isStoreScoped, isImpersonating, impersonatedOrg?.id, impersonatedOrgFull, isVendor, vendorBrandName, brandFilter, vendorCanBrowseOtherBrands]);

  const { data: brandRegistry } = useBrandRegistry();
  const catalogBrandFilter = useMemo(() => {
    if (isStoreScoped || (isVendor && !vendorCanBrowseOtherBrands) || !brandFilter) return brandFilter;
    return resolveBrandDisplayName(brandFilter, brandRegistry || []);
  }, [isStoreScoped, isVendor, vendorCanBrowseOtherBrands, brandFilter, brandRegistry]);
  const vendorGrantedBrandNames = useMemo(() => {
    if (!vendorCanBrowseOtherBrands || !Array.isArray(vendorCatalogScope.allowedBrands)) {
      return [] as string[];
    }
    return vendorCatalogScope.allowedBrands
      .map((id) => resolveBrandDisplayName(id, brandRegistry || []) || id)
      .filter(Boolean);
  }, [vendorCanBrowseOtherBrands, vendorCatalogScope.allowedBrands, brandRegistry]);
  const selectedBrandFilters = isStoreScoped ? storeBrandFilters : (catalogBrandFilter ? [catalogBrandFilter] : []);
  const effectiveBrandFilter = isStoreScoped
    ? (selectedBrandFilters.length > 0 ? selectedBrandFilters.join(',') : '')
    : isVendor && !vendorCanBrowseOtherBrands
      ? vendorBrandName
      : isVendor && vendorGrantedBrandNames.length > 0
        ? (catalogBrandFilter || vendorGrantedBrandNames.join(','))
        : catalogBrandFilter;
  // Directory logos live on `/admin/brands` (the `brands` collection). The
  // product Brand dropdown only consulted `/brand-registry`, which is a much
  // smaller set and often has null logoUrl — Hasselblad/OM System showed an
  // initial while the directory row for the same brand had a logo. Reuse the
  // admin query so both surfaces share one cache.
  const { data: directoryBrandsData } = useQuery({
    queryKey: ['admin', 'brands'],
    queryFn: () => brandManagementApi.listBrands(),
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  // Resolve store brand IDs to names (for dropdown and API). Products use brand names.
  const storeBrandNames = useMemo(() => {
    if (!isStoreWithBrands) return [] as string[];
    const names: string[] = [];
    for (const id of storeBrandIds) {
      const idKey = brandIdentityKey(id);
      const brand = (brandRegistry || []).find(
        (b) =>
          brandsMatchIdentity(b.id, id) ||
          brandsMatchIdentity(b.name, id) ||
          brandsMatchIdentity(b.slug, id) ||
          brandIdentityKey(b.id) === idKey ||
          brandIdentityKey(b.name) === idKey ||
          brandIdentityKey(b.slug) === idKey
      );
      if (brand?.name) names.push(brand.name);
      else names.push(id); // fallback to raw id if no registry match
    }
    return names.filter((name, index) => names.indexOf(name) === index).sort();
  }, [isStoreWithBrands, storeBrandIds, brandRegistry]);

  const brandLogoMap = useMemo(() => {
    const normalizeBrandKey = (value?: string) =>
      String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
    const map = new Map<string, string>();
    const addLogo = (key: string | undefined, url: string | null | undefined) => {
      if (!key || !url) return;
      const normalized = normalizeBrandKey(key);
      if (!normalized || map.has(normalized)) return;
      map.set(normalized, normalizeWikimediaThumbUrl(url));
    };
    (directoryBrandsData?.brands || []).forEach((brand) => {
      addLogo(brand.id, brand.logoUrl);
      addLogo(brand.slug, brand.logoUrl);
      addLogo(brand.name, brand.logoUrl);
    });
    (brandRegistry || []).forEach((brand) => {
      addLogo(brand.slug, brand.logoUrl);
      addLogo(brand.name, brand.logoUrl);
    });
    // Lumix can reuse the Panasonic mark when no Lumix logo is seeded.
    if (!map.has('lumix') && map.has('panasonic')) {
      map.set('lumix', map.get('panasonic') as string);
    }
    return map;
  }, [brandRegistry, directoryBrandsData]);

  const getBrandLogo = (brand?: string): string => {
    const normalizeBrandKey = (value?: string) =>
      String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
    const key = normalizeBrandKey(brand);
    const direct = brandLogoMap.get(key);
    if (direct) {
      return direct;
    }
    const fuzzy = Array.from(brandLogoMap.entries()).find(
      ([mappedKey]) => key.includes(mappedKey) || mappedKey.includes(key)
    )?.[1];
    return fuzzy || '/placeholder-camera.svg';
  };

  // Prices always carry an explicit currency label ("$149.00 USD" /
  // "$199.00 CAD"). The CA catalog can hold mixed currencies — e.g. NiSi is
  // USD distributor MSRP from the North American distributor, while .ca
  // sources are CAD — so a bare "$149.00" would be ambiguous. Formatting
  // depends on LocaleContext, so it's a hook shared with the product-detail
  // dialog rather than a page-local closure.
  const {
    formatAmountForProduct,
    formatProductPrice,
    saleDisplay,
    rebateDisplay,
    dealerReimbursement,
    getPricingStatus,
  } = useProductPriceFormatters();

  interface CardOfferLine {
    amount: string;
    label: string;
    ends?: string;
    endsAmber?: boolean;
    upcoming?: boolean;
  }

  interface CardPricing {
    hasPrice: boolean;
    netLabel: string;
    retailLabel: string;
    netNote: string;
    netIsRebate: boolean;
    offerLines: CardOfferLine[];
    mapMissing: boolean;
    mapLine: string;
    /** Combo/sold-with rebates exist but never move this item's own price. */
    hasComboOffers: boolean;
    /** For List view's compact "Ends {date}" flag. */
    rebateEndsLabel: string;
    rebateEndsAmber: boolean;
    rebateUpcoming: boolean;
  }

  /**
   * Net price leads, struck retail beside it, every discount itemized below —
   * one line per offer (sale / instant rebate / dealer reimbursement), each amount
   * computed from the real numbers so it can't drift from the struck price.
   */
  const buildCardPricing = (camera: CameraWithPricing): CardPricing => {
    const sale = saleDisplay(camera);
    const rebate = rebateDisplay(camera);
    const em = getEffectiveMapForProduct(camera);
    const rebateActive = em?.windowStatus === 'active' && rebate.instantRebate > 0;
    const hasStrike = sale.isSaleActive && sale.regularPrice > 0 && !!sale.regularLabel;

    const offerLines: CardOfferLine[] = [];
    if (hasStrike) {
      const rebatePortion = rebateActive ? Math.min(em!.instantRebate, sale.savingsAmount) : 0;
      const saleDiscount = sale.savingsAmount - rebatePortion;
      if (saleDiscount >= 1) {
        offerLines.push({ amount: formatAmountForProduct(camera, saleDiscount), label: 'sale discount' });
      }
    }

    let rebateEndsLabel = '';
    let rebateEndsAmber = false;
    let rebateUpcoming = false;
    if (rebateActive) {
      const endLabel = formatDate(rebate.endDate);
      const daysLeft = daysUntilDate(rebate.endDate);
      rebateEndsAmber = daysLeft != null && daysLeft <= 7;
      rebateEndsLabel = endLabel
        ? `ends ${endLabel}${daysLeft != null && daysLeft >= 0 ? `, ${daysLeft} day${daysLeft === 1 ? '' : 's'}` : ''}`
        : '';
      offerLines.push({
        amount: formatAmountForProduct(camera, rebate.instantRebate),
        label: 'instant rebate',
        ends: rebateEndsLabel || undefined,
        endsAmber: rebateEndsAmber,
      });
    } else if (em?.windowStatus === 'upcoming' && (em.instantRebate ?? 0) > 0) {
      // Not live yet, but getEffectiveMap already treats active + upcoming
      // alike for afterIR — a rebate that hasn't started shouldn't vanish
      // from the card until day one, it should just read "starts"/"from"
      // instead of "ends". em.instantRebate is the raw (ungated) amount;
      // rebate.instantRebate is 0 here since resolveRebateDisplay gates it
      // to 'active' only.
      // The caption feeds both layouts: the card reads it as the offer line's
      // trailing note, and List chips the same text. Leaving rebateEndsLabel
      // unset here showed upcoming rebates in List as a bare amount with no
      // hint that the window has not started. Not amber — upcoming is not
      // expiring-soon urgency.
      rebateEndsLabel = rebateWindowCaption(em, formatDate) || '';
      rebateUpcoming = true;
      offerLines.push({
        amount: formatAmountForProduct(camera, em.instantRebate),
        label: 'instant rebate',
        ends: rebateEndsLabel || undefined,
        upcoming: true,
      });
    }
    if (rebate.tradeCredit > 0) {
      // Despite the field name, this is the manufacturer's dealer
      // reimbursement credit (from the rebate program), not a customer
      // trade-in — the old "trade-in credit" copy was already wrong,
      // itemizing it here just made that more visible.
      offerLines.push({ amount: formatAmountForProduct(camera, rebate.tradeCredit), label: 'dealer reimbursement' });
    }

    // Combo rebates are shown but deliberately excluded from offerLines: they
    // require buying another item, so they must not move this item's net price
    // (see #120). Same predicate ComboRebateOffersNote renders on.
    const hasComboOffers = (camera.pricingData?.comboOffers || []).some(
      (offer) => offer.instantRebate > 0 && offer.status !== 'expired',
    );

    const mapMissing = !em?.raw;
    const mapDateLabel = em?.mapAsOf ? formatDate(em.mapAsOf) : null;
    const mapLine = !mapMissing ? `MAP ${formatAmountForProduct(camera, em!.floor)}${mapDateLabel ? ` · ${mapDateLabel}` : ''}` : '';

    return {
      hasPrice: !!formatProductPrice(camera),
      netLabel: sale.priceLabel,
      retailLabel: hasStrike ? sale.regularLabel : '',
      netNote: rebateActive ? 'after rebate' : '',
      netIsRebate: rebateActive,
      offerLines,
      hasComboOffers,
      mapMissing,
      mapLine,
      rebateEndsLabel,
      rebateEndsAmber,
      rebateUpcoming,
    };
  };

  // `?integrationId=` means this page is editing one integration's own sync
  // selection instead of the store-wide one. Ignored while the flag is off for
  // the org, so a stale link never produces a 403 from the API.
  const { isEnabled: isOrgFlagEnabled } = useOrgFeatureFlags();
  const perIntegrationSyncSelectionEnabled = isOrgFlagEnabled('per_integration_sync_selection');
  const compareEnabled = isStoreScoped && isOrgFlagEnabled('product_comparison');
  // Pre-order chip on the product card (flag `preorder_button`): the store shell only.
  const preorderChipEnabled = isStoreScoped && isOrgFlagEnabled('preorder_button');
  const addedFilterEnabled = isStoreScoped && isOrgFlagEnabled('product_browser_added_filter');
  // Off (or not a store): the filter never reaches the request, even from a stale saved view.
  const addedSince = addedFilterEnabled ? addedFilterToSince(addedFilter) : undefined;
  // Pricing-only placeholders (flag `placeholder_link_only`): a section on this picker, never a new page.
  const placeholdersEnabled = isStoreScoped && isOrgFlagEnabled('placeholder_link_only');
  const variantGroupSelectEnabled = isStoreScoped && isOrgFlagEnabled('sync_select_variant_group');
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  // Store product upload (flag `store_product_upload`): products this store adds itself, private to it.
  const productUploadFlag = isStoreScoped && isOrgFlagEnabled('store_product_upload');
  const [showProductUpload, setShowProductUpload] = useState(false);
  // Customer product submissions (flag `product_submissions`): a store or vendor submits a product the
  // catalog does not carry; private to the submitter until Motzu promotes it. Same section for both shells.
  const submissionScope: 'store' | 'vendor' | null = isStoreScoped ? 'store' : isVendor ? 'vendor' : null;
  const productSubmissionsEnabled = submissionScope !== null && isOrgFlagEnabled('product_submissions');
  // One entry point (MOT-695): with submissions on, "Add missing product" also offers
  // "keep this private", so the store upload's own section is not shown beside it.
  const productUploadEnabled = productUploadFlag && !productSubmissionsEnabled;
  const [showProductSubmissions, setShowProductSubmissions] = useState(false);
  const selectionIntegrationId = perIntegrationSyncSelectionEnabled
    ? searchParams.get('integrationId') || null
    : null;
  // Flag `store_browser_all_synced`: the rows depend on the selection, so every read names it.
  const allSyncedEnabled = isStoreScoped && isOrgFlagEnabled('store_browser_all_synced');
  const rowsIntegrationId = allSyncedEnabled ? selectionIntegrationId : null;
  const syncIssuesEnabled = isStoreScoped && isOrgFlagEnabled('product_browser_sync_issues');
  const { data: syncIssues, isLoading: syncIssuesLoading, isError: syncIssuesError, refetch: refetchSyncIssues } = useQuery({
    queryKey: ['store-product-sync-issues', storeOrg?.id, selectionIntegrationId],
    queryFn: () => apiService.getStoreProductSyncIssues(selectionIntegrationId),
    enabled: syncIssuesEnabled && !!storeOrg?.id,
    staleTime: 30_000,
    refetchInterval: syncIssuesEnabled ? 60_000 : false,
  });
  const syncIssueIds = useMemo(() => new Set((syncIssues || []).map((issue) => issue.productId)), [syncIssues]);
  const syncIssueReasons = useMemo(() => {
    const reasons = new Map<string, Set<string>>();
    for (const issue of syncIssues || []) {
      const messages = reasons.get(issue.productId) || new Set<string>();
      messages.add(issue.reason || issue.skipCode?.replace(/_/g, ' ') || 'Sync failed');
      reasons.set(issue.productId, messages);
    }
    return new Map(Array.from(reasons, ([id, messages]) => [id, Array.from(messages).join('; ')]));
  }, [syncIssues]);
  useEffect(() => {
    if (!syncIssuesEnabled) setSyncStatusFilter((value) => value === 'sync_issues' ? 'all' : value);
  }, [syncIssuesEnabled]);
  // "Matched to my store" (flag `product_browser_match_filter`). Off, or not a store: never applied,
  // even from a stale saved view.
  const matchFilterEnabled = isStoreScoped && isOrgFlagEnabled('product_browser_match_filter');
  const effectiveMatchFilter: MatchFilterValue = matchFilterEnabled ? matchFilter : 'all';
  // Pre-release lifecycle (flag `preorder_lifecycle`): the Announced / Pre-order chip and the
  // lifecycle filter. A store sees them with the flag on (the API serves `lifecycle` on its rows);
  // a platform admin always does, from the pre-release set joined onto the catalog rows below,
  // because the `/products*` reads it uses are shared with API keys and carry no lifecycle.
  // A vendor sees neither.
  const adminLifecycleMode = isPlatformAdmin && !isStoreScoped;
  const lifecycleEnabled = (isStoreScoped && isOrgFlagEnabled('preorder_lifecycle')) || adminLifecycleMode;
  const effectiveLifecycleFilter: LifecycleFilterValue = lifecycleEnabled ? lifecycleFilter : 'all';
  const {
    data: adminPreReleaseProducts,
    isLoading: adminPreReleaseLoading,
    error: adminPreReleaseError,
    refetch: refetchAdminPreRelease,
  } = useQuery({
    queryKey: ['admin-pre-release-products'],
    queryFn: () => apiService.getAdminPreReleaseProducts(),
    enabled: adminLifecycleMode,
    staleTime: 5 * 60 * 1000,
  });
  const adminLifecycleById = useMemo(
    () => new Map((adminPreReleaseProducts || []).flatMap((p) => (p.lifecycle ? [[p.id, p.lifecycle] as const] : []))),
    [adminPreReleaseProducts],
  );
  // The admin's pre-release views list that set whole (a few products) instead of a server
  // page, since no catalog read can filter on lifecycle; the page's other filters apply to it
  // client-side. A "released" pick (store-only, but a saved view can carry it) stays a server
  // page with the pre-release rows taken off it.
  const adminPreReleaseGrid =
    adminLifecycleMode && effectiveLifecycleFilter !== 'all' && effectiveLifecycleFilter !== 'released';
  // The admin's "Released" pick is a server page with the pre-release products left out before it
  // is cut (`includeUnreleased=false`, MOT-699), so its total, page and facet counts agree.
  const adminExcludeUnreleased = adminLifecycleMode && effectiveLifecycleFilter === 'released';
  const { data: crosswalkMatchedIdList, isError: crosswalkMatchedError } = useQuery({
    queryKey: ['store-crosswalk-matched-ids', selectionIntegrationId],
    queryFn: () => apiService.getCrosswalkMatchedProductIds(selectionIntegrationId),
    enabled: matchFilterEnabled,
    staleTime: 60 * 1000,
  });
  const crosswalkMatchedIds = useMemo(
    () => (crosswalkMatchedIdList ? new Set<string>(crosswalkMatchedIdList) : null),
    [crosswalkMatchedIdList]
  );

  const { data: storeSyncSelection } = useQuery({
    queryKey: ['store-sync-selection', selectionIntegrationId],
    queryFn: () => apiService.getStoreSyncSelection(selectionIntegrationId),
    enabled: isStoreScoped,
    staleTime: 30 * 1000,
  });

  const syncSelectedIds = useMemo(
    () => new Set<string>(storeSyncSelection?.selectedProductIds || []),
    [storeSyncSelection?.selectedProductIds]
  );

  // Flag `store_browser_all_synced`: a selection change can add or drop rows, whichever
  // card or dialog made it, and these queries hold no selection in their keys.
  const lastSelectionRef = useRef<{ integrationId: string | null; ids: string } | null>(null);
  useEffect(() => {
    if (!allSyncedEnabled || !storeSyncSelection) return;
    const ids = (storeSyncSelection.selectedProductIds || []).join(',');
    const last = lastSelectionRef.current;
    lastSelectionRef.current = { integrationId: selectionIntegrationId, ids };
    if (!last || last.integrationId !== selectionIntegrationId || last.ids === ids) return;
    // The universe first: a grid that reads it must not be served the stale copy.
    queryClient.invalidateQueries({ queryKey: ['storeBrandUniverse'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['storeCatalogCounts'] });
  }, [allSyncedEnabled, storeSyncSelection, selectionIntegrationId, queryClient]);

  // When the products a store adds to the selection will actually push: the
  // schedule of the integration this page edits, else the one the dashboard reads.
  const { data: storeIntegrations } = useStoreIntegrations(isStoreScoped);
  const nextSyncCopy = useMemo(() => {
    const integration = pickScheduleIntegration(storeIntegrations || [], selectionIntegrationId);
    return describeNextScheduledSync(integration?.syncSchedule, integration?.platform);
  }, [storeIntegrations, selectionIntegrationId]);

  // For stores, load the full scoped product universe once (all product types) to
  // derive stable brand options plus real per-tab/per-brand facet counts client-side —
  // one fetch instead of re-querying per tab. Refetches on catalogStatus so the
  // "{shown} of {total}" denominator and facet counts stay accurate once Status
  // widens past Active (matching admin's getProductMetadata(catalogStatus)).
  //
  // The request status is widened past Active (see storeRequestCatalogStatus) so the
  // universe also carries discontinued rows; everything derived from it is narrowed
  // back through filterStoreCatalogRows.
  const storeUniverseRequestStatus = storeRequestCatalogStatus(catalogStatus);
  // Counts follow the picked status, not the widened one: asking for `all` here
  // would total archived products the grid is not showing and overstate every
  // badge. The endpoint applies the default-view rule itself, counting the
  // discontinued products this store still syncs, so these badges match the grid
  // whether or not it widened — and they carry `discontinuedInSync`, which is what
  // decides that widening.
  const {
    data: storeCatalogCounts,
    isLoading: storeCountsLoading,
    isError: storeCountsIsError,
    isSuccess: storeCountsIsSuccess,
  } = useQuery({
    queryKey: ['storeCatalogCounts', catalogStatus, selectionIntegrationId],
    queryFn: () =>
      apiService.getStoreCatalogCounts({
        catalogStatus,
        integrationId: selectionIntegrationId,
      }),
    enabled: isStoreScoped,
    staleTime: 60000,
    gcTime: 10 * 60 * 1000,
  });
  const storeDataSource = useMemo(
    () =>
      resolveStoreBrowserDataSource({
        isStoreScoped,
        catalogStatus,
        syncStatusFilter,
        discontinuedInSyncOnly,
        // The lifecycle filter is applied client-side like the match filter, so it needs the full set too.
        matchFilterActive: effectiveMatchFilter !== 'all' || effectiveLifecycleFilter !== 'all',
        storeCatalogCounts,
        storeCountsStatus: {
          isLoading: storeCountsLoading,
          isError: storeCountsIsError,
          isSuccess: storeCountsIsSuccess,
        },
      }),
    [
      isStoreScoped,
      catalogStatus,
      syncStatusFilter,
      discontinuedInSyncOnly,
      effectiveMatchFilter,
      effectiveLifecycleFilter,
      storeCatalogCounts,
      storeCountsLoading,
      storeCountsIsError,
      storeCountsIsSuccess,
    ]
  );
  const {
    storeWidensForSyncedDiscontinued,
    shouldUseFullStoreSyncDataset,
    needsStoreUniverse,
    tabCountsFromUniverse,
    awaitingStoreCounts,
    gridRequest: storeGridRequest,
  } = storeDataSource;
  const { data: storeBrandUniverse, isLoading: storeUniverseLoading } = useQuery({
    ...storeUniverseQuery(storeUniverseRequestStatus, rowsIntegrationId),
    enabled: needsStoreUniverse,
  });
  // The client-paginated grid with no other filter is the universe request itself.
  const gridReadsUniverse = isStoreScoped && gridRequestReadsUniverse(storeGridRequest, {
    search: debouncedSearch,
    category: categoryFilter,
    productType,
    brand: effectiveBrandFilter,
    addedSince: addedFilterEnabled ? addedFilterToSince(addedFilter) : undefined,
    sort,
  });

  const storeUniverseRaw = useMemo<Product[]>(
    () => (Array.isArray(storeBrandUniverse?.data) ? (storeBrandUniverse!.data.filter(Boolean) as Product[]) : []),
    [storeBrandUniverse?.data]
  );

  /** Universe narrowed to what the current Status pick actually shows. */
  const storeUniverseAll = useMemo<Product[]>(
    () => filterStoreCatalogRows(storeUniverseRaw, catalogStatus, syncSelectedIds),
    [storeUniverseRaw, catalogStatus, syncSelectedIds]
  );

  const { data: camerasData, isLoading, error, isFetching } = useQuery({
    queryKey: [
      'products',
      debouncedPage,
      debouncedSearch,
      effectiveBrandFilter,
      categoryFilter,
      productType, // queryKey[5] — see PRODUCTS_QUERY_KEY_INDEX; placeholderData reads it
      catalogStatus,
      limit,
      isVendor,
      isStoreScoped,
      isStoreWithBrands,
      // syncStatusFilter is deliberately not part of the key: the request only
      // depends on whether it forces the client-paginated full dataset (below),
      // so "In sync" and "Not in sync" share one cached response instead of
      // refetching the whole store catalog on every toggle. The filter itself
      // is applied client-side in filteredCameras.
      shouldUseFullStoreSyncDataset, // queryKey[11] — see PRODUCTS_QUERY_KEY_INDEX
      addedFilterEnabled ? addedFilter : '',
      sort, // appended after the indexed positions above so they stay put
      // The server-side default-view rule and the selection it reads (MOT-607).
      storeGridRequest.includeSyncedDiscontinued,
      storeGridRequest.includeSyncedDiscontinued ? selectionIntegrationId ?? null : rowsIntegrationId,
      adminExcludeUnreleased,
    ],
    // The default view no longer waits for the counts call: the server merges
    // the synced discontinued rows into the page it cuts (MOT-607), so #161's
    // "still loading means syncs nothing discontinued" can no longer happen.
    enabled: (!isStoreScoped || storeGridRequest.ready) && !adminPreReleaseGrid,
    queryFn: async ({ signal }) => {
      // For stores with dealer associations: use store-scoped API (only their brands)
      if (isStoreScoped) {
        // The whole dataset with no other filter is what the universe query holds:
        // read that query (deduplicated with its request in flight, served from
        // its cache while fresh) rather than fetching the same URL twice (MOT-628).
        if (gridReadsUniverse) {
          return await queryClient.fetchQuery(storeUniverseQuery(storeGridRequest.catalogStatus, rowsIntegrationId));
        }
        // For sync-status filtering, fetch the full scoped set and paginate client-side
        // so in-sync/not-in-sync reflects all products, not only the current page chunk.
        // Sync-status filters need the full brand-scoped set client-side; keep under API max (10000).
        const requestLimit = storeGridRequest.clientPaginated ? 10000 : limit;
        const requestOffset = storeGridRequest.clientPaginated ? 0 : (debouncedPage - 1) * limit;
        // Only widen past the picked status when we paginate client-side. Widening a
        // server-paginated request would return rows this view filters out, leaving
        // short pages.
        return await apiService.getStoreProducts(requestLimit, requestOffset, {
          search: debouncedSearch.trim() || undefined,
          category: categoryFilter || undefined,
          productType,
          brand: effectiveBrandFilter || undefined,
          catalogStatus: storeGridRequest.catalogStatus,
          // Resolved here, not in the key, so a rolling preset is current on every fetch.
          addedSince: addedFilterEnabled ? addedFilterToSince(addedFilter) : undefined,
          sort,
          // The default Active view: one server-cut page that already carries the
          // discontinued products this store still syncs (MOT-607).
          includeSyncedDiscontinued: storeGridRequest.includeSyncedDiscontinued,
          integrationId: storeGridRequest.includeSyncedDiscontinued ? selectionIntegrationId : rowsIntegrationId,
          signal,
        });
      }

      // For vendors, always filter by their brand
      const brandToUse = effectiveBrandFilter;

      // Priority: Search > Brand+Category > Brand > Category > All
      if (debouncedSearch.trim()) {
        try {
          return await apiService.searchProducts(
            debouncedSearch,
            limit,
            (debouncedPage - 1) * limit,
            productType,
            catalogStatus,
            brandToUse || undefined,
            sort,
            signal,
            adminExcludeUnreleased ? false : undefined
          );
        } catch (error) {
          console.warn('Search endpoint failed, falling back to brand/category filtering');
          // Fall back to brand/category filtering if search fails
        }
      }

      if (brandToUse && categoryFilter) {
        return await apiService.getProductsByBrand(
          brandToUse,
          limit,
          (debouncedPage - 1) * limit,
          categoryFilter,
          productType,
          catalogStatus,
          sort,
          adminExcludeUnreleased ? false : undefined
        );
      }

      if (brandToUse) {
        return await apiService.getProductsByBrand(
          brandToUse,
          limit,
          (debouncedPage - 1) * limit,
          undefined,
          productType,
          catalogStatus,
          sort,
          adminExcludeUnreleased ? false : undefined
        );
      }

      if (categoryFilter) {
        return await apiService.getProductsByCategory(categoryFilter, limit, (debouncedPage - 1) * limit, productType, catalogStatus, sort, adminExcludeUnreleased ? false : undefined);
      }

      return await apiService.getProducts(limit, (debouncedPage - 1) * limit, productType, catalogStatus, sort, adminExcludeUnreleased ? false : undefined);
    },
    // A client-paginated request reads the whole brand scope: never retried
    // (the universe query has its own rule), and kept as long as the universe.
    retry: storeGridRequest.clientPaginated ? 0 : 1,
    staleTime: storeGridRequest.clientPaginated ? STORE_UNIVERSE_FRESH_MS : 30000, // Data stays fresh for 30 seconds
    gcTime: storeGridRequest.clientPaginated ? STORE_UNIVERSE_FRESH_MS : 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    placeholderData: (previousData, previousQuery) => {
      // Keep the last page while search/pagination refetch, but drop it when
      // the product-type tab changes or the request switches between a server
      // page and the client-paginated full dataset (the sync-status filter),
      // so the grid shows skeletons instead of the previous rows filtered
      // down to a misleading handful with no loading indicator.
      const keep = canKeepPreviousProductsPage(
        previousQuery ? productsPlaceholderShapeFromKey(previousQuery.queryKey) : undefined,
        { productType, clientPaginated: shouldUseFullStoreSyncDataset },
      );
      return keep ? previousData : undefined;
    },
  });

  const { data: allProducts, isLoading: productMetaLoading } = useQuery({
    queryKey: ['productMetadata', catalogStatus, adminExcludeUnreleased],
    queryFn: () => apiService.getProductMetadata(catalogStatus, adminExcludeUnreleased ? false : undefined),
    enabled: !isStoreScoped,
    staleTime: 60000,
    gcTime: 10 * 60 * 1000,
  });

  /**
   * Which source the facet badges read.
   *
   * Whenever the universe is fetched the badges come from it, because only the
   * client can narrow those rows to what the default view shows. The counts
   * endpoint is for the states that never fetch it — it matches the grid there,
   * and would disagree with a widened grid request.
   */
  const tabCountsLoading = isStoreScoped
    ? tabCountsFromUniverse
      ? storeUniverseLoading
      : storeCountsLoading
    : productMetaLoading;

  const categoryBrandFilter =
    isStoreScoped && selectedBrandFilters.length !== 1
      ? ''
      : (selectedBrandFilters[0] || effectiveBrandFilter);

  // Get categories for the selected brand
  const { data: brandCategories } = useQuery({
    queryKey: ['brandCategories', categoryBrandFilter, productType],
    queryFn: () =>
      categoryBrandFilter ? apiService.getCategoriesForBrand(categoryBrandFilter, productType) : Promise.resolve([]),
    enabled: !!categoryBrandFilter,
    staleTime: 60000, // Categories stay fresh for 1 minute
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  // Get all available brands from products (admins/vendors) or from store's dealer associations
  const allBrands = useMemo(() => {
    if (allProducts?.brands) {
      return mergeBrandLabels(allProducts.brands.map((b: any) => b.brand).filter(Boolean));
    }
    return [] as string[];
  }, [allProducts]);

  // For stores, build brand options from registry names + product brands.
  // Case-insensitive dedupe (avoid SmallRig + Smallrig from slug title-casing).
  const storeScopedBrands = useMemo(() => {
    if (!isStoreScoped) return [] as string[];
    const universeSource = storeBrandUniverse?.data;
    const universeData = Array.isArray(universeSource) ? universeSource : [];
    const fromUniverseResults = universeData.map((camera: any) => camera?.brand).filter(Boolean) as string[];
    const scopedSource = camerasData?.data;
    const scopedData = Array.isArray(scopedSource) ? scopedSource : [];
    const fromScopedResults = scopedData.map((camera: any) => camera?.brand).filter(Boolean) as string[];

    // A brand the store syncs but has not listed comes only from the counts when no row of it is on this page.
    const fromCounts = (storeCatalogCounts?.brands || []).map((entry: { brand?: string }) => entry.brand).filter(Boolean) as string[];

    return mergeBrandLabels([...storeBrandNames, ...fromCounts, ...fromUniverseResults, ...fromScopedResults]);
  }, [isStoreScoped, storeBrandNames, storeCatalogCounts?.brands, storeBrandUniverse?.data, camerasData?.data]);

  // Keep selected store brand filters valid whenever available brand options change.
  useEffect(() => {
    if (!isStoreScoped) return;
    setStoreBrandFilters((previous) => {
      const next = previous
        .map((brand) => storeScopedBrands.find((option) => brandsMatchIdentity(option, brand)))
        .filter((brand): brand is string => !!brand)
        .filter((brand, index, arr) => arr.indexOf(brand) === index);
      if (
        next.length === previous.length &&
        next.every((brand, index) => brand === previous[index])
      ) {
        return previous;
      }
      return next;
    });
  }, [isStoreScoped, storeScopedBrands]);

  // For stores with dealer associations, use only their brands. Manufacturers
  // stay on their own brand. Distributors (and granted catalogBrandIds) can
  // pick from the catalog.
  const brands = useMemo(() => {
    if (isStoreScoped && storeScopedBrands.length > 0) {
      return storeScopedBrands;
    }
    if (isVendor && vendorBrandName && !vendorCanBrowseOtherBrands) {
      return [vendorBrandName];
    }
    if (isVendor && vendorGrantedBrandNames.length > 0) {
      return vendorGrantedBrandNames;
    }
    if (allBrands.length === 0 && brandRegistry && brandRegistry.length > 0) {
      return brandRegistry.map((brand) => brand.name).filter(Boolean).sort();
    }
    return allBrands;
  }, [allBrands, isStoreScoped, storeScopedBrands, isVendor, vendorBrandName, vendorCanBrowseOtherBrands, vendorGrantedBrandNames, brandRegistry]);

  useEffect(() => {
    if (isStoreScoped || (isVendor && !vendorCanBrowseOtherBrands) || !brandFilter) return;
    const resolved = resolveBrandDisplayName(brandFilter, [
      ...(brandRegistry || []),
      ...brands,
    ]);
    if (resolved && resolved !== brandFilter) setBrandFilter(resolved);
  }, [isStoreScoped, isVendor, vendorCanBrowseOtherBrands, brandFilter, brandRegistry, brands]);

  // Use brand-specific categories if brand is selected, otherwise use all categories from products
  const categories = useMemo(() => {
    if (categoryBrandFilter && brandCategories?.length) return brandCategories;
    if (!isStoreScoped && allProducts?.categories) {
      return allProducts.categories.map((c: any) => c.category).filter(Boolean).sort() as string[];
    }
    // For stores, derive from current page data
    const source = camerasData?.data;
    const rawData = Array.isArray(source) ? source.filter(Boolean) : [];
    return Array.from(new Set(rawData.map((c: any) => c?.category).filter(Boolean))).sort() as string[];
  }, [categoryBrandFilter, brandCategories, allProducts, isStoreScoped, camerasData?.data]);

  const { data: pricingPreferences } = useQuery<{ allowBelowMap?: boolean }>({
    queryKey: ['pricing-preferences', impersonatedOrg?.id || 'self'],
    queryFn: async () => {
      const { makeApiCall, getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/pricing-preferences', { headers });
      if (!response.ok) return { allowBelowMap: false };
      const json = await response.json();
      return json.preferences || json.data || { allowBelowMap: false };
    },
    enabled: isStoreScoped,
    staleTime: 60 * 1000,
  });

  // Only worth asking "does price vary by location" when the store has more
  // than one active location.
  const { data: storeLocationCount } = useQuery<number>({
    queryKey: ['store-locations-count', impersonatedOrg?.id || 'self'],
    queryFn: async () => {
      const { makeApiCall, getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/locations', { headers });
      if (!response.ok) return 1;
      const json = await response.json();
      const locations = Array.isArray(json.data) ? json.data : [];
      return locations.filter((l: { isActive?: boolean }) => l.isActive !== false).length;
    },
    enabled: isStoreScoped,
    staleTime: 5 * 60 * 1000,
  });
  const hasMultipleStoreLocations = isStoreScoped && (storeLocationCount ?? 0) > 1;

  // --- Product Browser filter-bar derived data -----------------------------
  // Facet counts are wired from real data wherever the API/loaded dataset
  // already carries them (product-type totals, brand/category counts); a
  // field with no real count source (Status; Category once brand-narrowed —
  // getCategoriesForBrand returns names only, no counts) ships without one
  // rather than fabricating a number.
  const numberFormat = (n: number): string => formatNumber(n);

  const productTypeCounts = useMemo(() => {
    const map = new Map<ProductTypeTabItem['value'], number>();
    if (isStoreScoped) {
      if (tabCountsFromUniverse) {
        // An empty universe is [] before the query resolves — don't publish
        // all=0 or the All products tab shows "0" while the others still skeleton.
        if (!storeBrandUniverse) return map;
        let all = 0;
        storeUniverseAll.forEach((p) => {
          all += 1;
          if (p.productType === 'camera' || p.productType === 'lens' || p.productType === 'accessory') {
            map.set(p.productType, (map.get(p.productType) || 0) + 1);
          }
        });
        map.set('all', all);
        return map;
      }
      if (!storeCatalogCounts) return map;
      (storeCatalogCounts.productTypes || []).forEach((t) => {
        if (t.productType === 'camera' || t.productType === 'lens' || t.productType === 'accessory') {
          map.set(t.productType, t.count);
        }
      });
      map.set('all', storeCatalogCounts.total || 0);
      return map;
    }
    if (allProducts?.productTypes) {
      allProducts.productTypes.forEach((t) => {
        if (t.productType === 'camera' || t.productType === 'lens' || t.productType === 'accessory') {
          map.set(t.productType, t.count);
        }
      });
      map.set('all', allProducts.total || 0);
    }
    return map;
  }, [isStoreScoped, tabCountsFromUniverse, storeBrandUniverse, storeUniverseAll, storeCatalogCounts, allProducts]);

  const productTypeTabs: ProductTypeTabItem[] = useMemo(
    () =>
      (
        [
          ['all', 'All products'],
          ['camera', 'Cameras'],
          ['lens', 'Lenses'],
          ['accessory', 'Accessories'],
        ] as const
      ).map(([value, label]) => ({
        value,
        label,
        count: productTypeCounts.has(value) ? numberFormat(productTypeCounts.get(value)!) : undefined,
      })),
    [productTypeCounts]
  );

  const brandCountMap = useMemo(() => {
    const byKey = new Map<string, number>();
    const add = (name: string, n: number) => {
      const key = brandFamilyKey(name);
      if (!key) return;
      byKey.set(key, (byKey.get(key) || 0) + n);
    };
    if (isStoreScoped) {
      if (tabCountsFromUniverse) {
        storeUniverseAll.forEach((p) => {
          if (p.brand) add(p.brand, 1);
        });
      } else {
        (storeCatalogCounts?.brands || []).forEach((b) => {
          if (b?.brand) add(b.brand, b.count || 0);
        });
      }
    } else {
      (allProducts?.brands || []).forEach((b) => {
        if (b?.brand) add(b.brand, b.count || 0);
      });
    }
    const map = new Map<string, number>();
    for (const brand of brands) {
      const n = byKey.get(brandFamilyKey(brand));
      if (n) map.set(brand, n);
    }
    return map;
  }, [isStoreScoped, tabCountsFromUniverse, storeUniverseAll, storeCatalogCounts, allProducts, brands]);

  const categoryCountsAvailable = !(categoryBrandFilter && (brandCategories?.length ?? 0) > 0);
  const categoryCountMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!categoryCountsAvailable) return map;
    if (isStoreScoped) {
      if (tabCountsFromUniverse) {
        storeUniverseAll.forEach((p) => {
          if (p.category) map.set(p.category, (map.get(p.category) || 0) + 1);
        });
        return map;
      }
      (storeCatalogCounts?.categories || []).forEach((c) => {
        if (c?.category) map.set(c.category, c.count || 0);
      });
      return map;
    }
    (allProducts?.categories || []).forEach((c) => {
      if (c?.category) map.set(c.category, c.count || 0);
    });
    return map;
  }, [categoryCountsAvailable, isStoreScoped, tabCountsFromUniverse, storeUniverseAll, storeCatalogCounts, allProducts]);

  // Real but approximate: reflects the store's whole sync-selection state,
  // not narrowed by the other active filters (no endpoint returns that).
  const syncCountMap = useMemo(() => {
    const denominator = resolveSyncCountDenominator({
      tabCountsFromUniverse,
      storeUniverseLength: storeUniverseAll.length,
      storeCatalogTotal: storeCatalogCounts?.total,
    });
    return buildSyncCountMap({
      isStoreScoped,
      inSyncCount: syncSelectedIds.size,
      denominator,
    });
  }, [
    isStoreScoped,
    syncSelectedIds,
    tabCountsFromUniverse,
    storeUniverseAll.length,
    storeCatalogCounts?.total,
  ]);

  /** Real logo (registry-backed or a known local asset) with a colored-initial fallback — never the product-photo placeholder silhouette. */
  const resolveBrandMark = (brand?: string): ProductFilterMark => {
    const info = getVendorInfo(brand);
    const dbLogo = brand ? getBrandLogo(brand) : '';
    const hasRealDbLogo = !!dbLogo && dbLogo !== '/placeholder-camera.svg';
    return {
      logoUrl: info.logo || (hasRealDbLogo ? dbLogo : undefined),
      initial: (brand || '?').trim().charAt(0).toUpperCase() || '?',
      color: info.color || theme.palette.primary.main,
    };
  };

  const brandPillOptions: ProductFilterOption[] = useMemo(
    () =>
      brands.map((brand) => ({
        value: brand,
        label: brand,
        hint: brandCountMap.has(brand) ? numberFormat(brandCountMap.get(brand)!) : undefined,
        mark: resolveBrandMark(brand),
      })),
    [brands, brandCountMap, brandLogoMap]
  );

  const categoryPillOptions: ProductFilterOption[] = useMemo(
    () =>
      categories.map((category) => ({
        value: category,
        label: category,
        hint: categoryCountMap.has(category) ? numberFormat(categoryCountMap.get(category)!) : undefined,
      })),
    [categories, categoryCountMap]
  );

  const STATUS_PILL_OPTIONS: ProductFilterOption[] = [
    { value: 'active', label: 'Active' },
    { value: 'archived', label: 'Archived' },
    { value: 'all', label: 'All statuses' },
  ];

  const syncPillOptions: ProductFilterOption[] = [
    { value: 'in_sync', label: 'In sync', hint: syncCountMap.has('in_sync') ? numberFormat(syncCountMap.get('in_sync')!) : undefined },
    { value: 'not_in_sync', label: 'Not in sync', hint: syncCountMap.has('not_in_sync') ? numberFormat(syncCountMap.get('not_in_sync')!) : undefined },
    ...(syncIssuesEnabled ? [{ value: 'sync_issues', label: 'Sync issues',
      hint: syncIssues ? numberFormat(tabCountsFromUniverse
        ? storeUniverseAll.filter((product) => syncIssueIds.has(product.id)).length : syncIssueIds.size) : undefined }] : []),
  ];

  const brandPillField: ProductFilterFieldDef = {
    key: 'brand',
    label: 'Brand',
    multi: isStoreScoped,
    exclusiveDefault: isStoreScoped,
    allLabel: 'All brands',
    // Say which statuses the counts beside each brand include; the Brand
    // Directory shows the same active count next to its all-status total.
    hint:
      catalogStatus === 'all'
        ? tx('productBrowser.brandCounts.all')
        : catalogStatus === 'archived'
          ? tx('productBrowser.brandCounts.archived')
          : isStoreScoped
            ? tx('productBrowser.brandCounts.activeStore')
            : tx('productBrowser.brandCounts.active'),
    searchable: true,
    searchPlaceholder: `Search ${brands.length} brand${brands.length === 1 ? '' : 's'}`,
    pinned: true,
    locked: isVendor && !isStoreScoped && !vendorCanBrowseOtherBrands,
  };
  const anyBrandApplied = isStoreScoped ? storeBrandFilters.length > 0 : !!catalogBrandFilter;
  const categoryPillField: ProductFilterFieldDef = {
    key: 'category',
    label: 'Category',
    multi: false,
    searchable: true,
    searchPlaceholder: 'Search categories',
    menuHint: 'follows brand',
    menuHintColor: anyBrandApplied ? 'primary' : 'muted',
  };
  const statusPillField: ProductFilterFieldDef = {
    key: 'status',
    label: 'Status',
    multi: false,
    pinned: true,
  };
  const addedPillField: ProductFilterFieldDef = {
    key: 'added',
    label: tx('productBrowser.addedFilter.label'),
    multi: false,
    allLabel: tx('productBrowser.addedFilter.anyTime'),
    hint: tx('productBrowser.addedFilter.hint'),
    menuHint: tx('productBrowser.addedFilter.menuHint'),
    menuHintColor: 'primary',
  };
  const addedPillOptions: ProductFilterOption[] = ADDED_FILTER_PRESETS.map((preset) => ({
    value: preset.value,
    label: tx(`productBrowser.addedFilter.last${preset.days}`),
  }));
  const addedOptionLabel = (v: string) =>
    v.startsWith('since:')
      ? tx('productBrowser.addedFilter.sinceDate', { date: v.slice('since:'.length) })
      : addedPillOptions.find((o) => o.value === v)?.label || v;
  const syncPillField: ProductFilterFieldDef = {
    key: 'sync',
    label: 'Store sync',
    multi: false,
    menuHint: 'store users',
    menuHintColor: 'muted',
  };

  const matchPillField: ProductFilterFieldDef = {
    key: 'match',
    label: tx('productBrowser.matchFilter.label'),
    multi: false,
    hint: tx('productBrowser.matchFilter.hint'),
    menuHint: tx('productBrowser.matchFilter.menuHint'),
    menuHintColor: 'primary',
  };
  // Counted over the store's scoped universe when it is loaded (it is, while the filter is set);
  // otherwise no number rather than one the grid will not show.
  const matchCounts = useMemo(() => {
    if (!crosswalkMatchedIds || !tabCountsFromUniverse) return null;
    let matchedInSync = 0;
    let matchedNotInSync = 0;
    storeUniverseAll.forEach((p) => {
      if (!crosswalkMatchedIds.has(p.id)) return;
      if (syncSelectedIds.has(p.id)) matchedInSync += 1;
      else matchedNotInSync += 1;
    });
    return {
      matched_in_sync: matchedInSync,
      matched_not_in_sync: matchedNotInSync,
      not_matched: storeUniverseAll.length - matchedInSync - matchedNotInSync,
    };
  }, [crosswalkMatchedIds, tabCountsFromUniverse, storeUniverseAll, syncSelectedIds]);
  const matchPillOptions: ProductFilterOption[] = (
    [
      ['matched_in_sync', 'matchedInSync'],
      ['matched_not_in_sync', 'matchedNotInSync'],
      ['not_matched', 'notMatched'],
    ] as const
  ).map(([value, key]) => ({
    value,
    label: tx(`productBrowser.matchFilter.${key}`),
    hint: matchCounts ? numberFormat(matchCounts[value]) : undefined,
  }));
  const matchOptionLabel = (v: string) => matchPillOptions.find((o) => o.value === v)?.label || v;
  const matchPillValue = matchFilter === 'all' ? '' : matchFilter;
  const handleMatchPillChange = (value: ProductFilterValue) => {
    const raw = typeof value === 'string' ? value : value[0];
    setMatchFilter(isMatchFilterValue(raw) ? raw : 'all');
  };

  const lifecyclePillField: ProductFilterFieldDef = {
    key: 'lifecycle',
    label: tx('productBrowser.lifecycleFilter.label'),
    multi: false,
    hint: tx('productBrowser.lifecycleFilter.hint'),
    menuHint: tx('productBrowser.lifecycleFilter.menuHint'),
    menuHintColor: 'primary',
  };
  const lifecycleCounts = useMemo((): Record<'announced' | 'preorder' | 'released', number | null> | null => {
    if (adminLifecycleMode) {
      if (!adminPreReleaseProducts) return null;
      const counts = { announced: 0, preorder: 0, released: null };
      adminPreReleaseProducts.forEach((p) => {
        const stage = lifecycleStageOf(p);
        if (stage !== 'released') counts[stage] += 1;
      });
      return counts;
    }
    if (!lifecycleEnabled || !tabCountsFromUniverse) return null;
    const counts = { announced: 0, preorder: 0, released: 0 };
    storeUniverseAll.forEach((p) => { counts[lifecycleStageOf(p)] += 1; });
    return counts;
  }, [adminLifecycleMode, adminPreReleaseProducts, lifecycleEnabled, tabCountsFromUniverse, storeUniverseAll]);
  // Every pick is offered to a store and to the admin shell. The admin's "Released" is a server page
  // read with `includeUnreleased=false`, so its total is not off by the pre-release rows (MOT-699).
  const lifecycleStageValues = ['prerelease', 'announced', 'preorder', 'released'] as const;
  const lifecyclePillOptions: ProductFilterOption[] = lifecycleStageValues.map((value) => {
    const count = !lifecycleCounts
      ? null
      : value === 'prerelease'
        ? (lifecycleCounts.announced ?? 0) + (lifecycleCounts.preorder ?? 0)
        : lifecycleCounts[value];
    return {
      value,
      label: value === 'prerelease' ? tx('productBrowser.lifecycleFilter.prerelease') : tx(`productLifecycle.stage.${value}`),
      hint: count != null ? numberFormat(count) : undefined,
    };
  });
  const lifecycleOptionLabel = (v: string) => lifecyclePillOptions.find((o) => o.value === v)?.label || v;
  const lifecyclePillValue = lifecycleFilter === 'all' ? '' : lifecycleFilter;
  const handleLifecyclePillChange = (value: ProductFilterValue) => {
    const raw = typeof value === 'string' ? value : value[0];
    setLifecycleFilter(isLifecycleFilterValue(raw) ? raw : 'all');
  };

  const brandPillValue: ProductFilterValue = isStoreScoped ? storeBrandFilters : catalogBrandFilter;
  const soleSelectedBrand = isStoreScoped
    ? (storeBrandFilters.length === 1 ? storeBrandFilters[0] : undefined)
    : (catalogBrandFilter || undefined);
  const brandLeadingMark = soleSelectedBrand ? resolveBrandMark(soleSelectedBrand) : undefined;
  const brandPillActive = isStoreScoped ? storeBrandFilters.length > 0 : !!catalogBrandFilter;

  const categoryHint = soleSelectedBrand
    ? `Showing the ${categoryPillOptions.length} categor${categoryPillOptions.length === 1 ? 'y' : 'ies'} ${soleSelectedBrand} carries`
    : null;

  const handleAddedPillChange = (value: ProductFilterValue) => {
    const raw = typeof value === 'string' ? value : value[0];
    setAddedFilter(isAddedFilterValue(raw) ? raw : '');
  };
  const syncPillValue = syncStatusFilter === 'all' ? '' : syncStatusFilter;

  const statusOptionLabel = (v: string) => STATUS_PILL_OPTIONS.find((o) => o.value === v)?.label || v;
  const syncOptionLabel = (v: string) => syncPillOptions.find((o) => o.value === v)?.label || v;

  const handleBrandPillChange = (value: ProductFilterValue) => {
    if (isStoreScoped) {
      setStoreBrandFilters(Array.isArray(value) ? value : value ? [value] : []);
    } else {
      setBrandFilter(typeof value === 'string' ? value : value[0] || '');
    }
  };
  const handleCategoryPillChange = (value: ProductFilterValue) => {
    setCategoryFilter(typeof value === 'string' ? value : value[0] || '');
  };
  const handleStatusPillChange = (value: ProductFilterValue) => {
    setCatalogStatus((typeof value === 'string' ? value : value[0]) as CatalogProductStatus);
  };
  const handleSyncPillChange = (value: ProductFilterValue) => {
    const raw = typeof value === 'string' ? value : value[0];
    setSyncStatusFilter(raw === 'in_sync' || raw === 'not_in_sync' || (raw === 'sync_issues' && syncIssuesEnabled) ? raw : 'all');
  };

  // Category and (for store users) Store sync start hidden behind "+ Add filter".
  const [addedPillKeys, setAddedPillKeys] = useState<ProductPillKey[]>([]);
  const [openPillKey, setOpenPillKey] = useState<ProductPillKey | null>(null);
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const [activeViewName, setActiveViewName] = useState<string | null>(null);

  // Restoring a category/sync value from the URL or a saved view should show
  // its pill even though the user didn't click "+ Add filter" this visit.
  useEffect(() => {
    if (!categoryFilter) return;
    setAddedPillKeys((prev) => (prev.includes('category') ? prev : [...prev, 'category']));
  }, [categoryFilter]);
  useEffect(() => {
    if (!addedFilter || !addedFilterEnabled) return;
    setAddedPillKeys((prev) => (prev.includes('added') ? prev : [...prev, 'added']));
  }, [addedFilter, addedFilterEnabled]);
  useEffect(() => {
    if (syncStatusFilter === 'all') return;
    setAddedPillKeys((prev) => (prev.includes('sync') ? prev : [...prev, 'sync']));
  }, [syncStatusFilter]);
  useEffect(() => {
    if (matchFilter === 'all' || !matchFilterEnabled) return;
    setAddedPillKeys((prev) => (prev.includes('match') ? prev : [...prev, 'match']));
  }, [matchFilter, matchFilterEnabled]);
  useEffect(() => {
    if (lifecycleFilter === 'all' || !lifecycleEnabled) return;
    setAddedPillKeys((prev) => (prev.includes('lifecycle') ? prev : [...prev, 'lifecycle']));
  }, [lifecycleFilter, lifecycleEnabled]);

  const addablePillFields: ProductFilterFieldDef[] = [
    categoryPillField,
    ...(isStoreScoped ? [syncPillField] : []),
    ...(addedFilterEnabled ? [addedPillField] : []),
    ...(matchFilterEnabled ? [matchPillField] : []),
    ...(lifecycleEnabled ? [lifecyclePillField] : []),
  ].filter(
    (f) => !addedPillKeys.includes(f.key)
  );

  const closeAllPillPopovers = () => {
    setOpenPillKey(null);
    setAddFilterOpen(false);
  };

  const handleAddPill = (key: ProductPillKey) => {
    setAddedPillKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setAddFilterOpen(false);
    setOpenPillKey(key);
  };
  const handleRemoveCategoryPill = () => {
    setCategoryFilter('');
    setAddedPillKeys((prev) => prev.filter((k) => k !== 'category'));
    setOpenPillKey((prev) => (prev === 'category' ? null : prev));
  };
  const handleRemoveAddedPill = () => {
    setAddedFilter('');
    setAddedPillKeys((prev) => prev.filter((k) => k !== 'added'));
    setOpenPillKey((prev) => (prev === 'added' ? null : prev));
  };
  const handleRemoveMatchPill = () => {
    setMatchFilter('all');
    setAddedPillKeys((prev) => prev.filter((k) => k !== 'match'));
    setOpenPillKey((prev) => (prev === 'match' ? null : prev));
  };
  const handleRemoveLifecyclePill = () => {
    setLifecycleFilter('all');
    setAddedPillKeys((prev) => prev.filter((k) => k !== 'lifecycle'));
    setOpenPillKey((prev) => (prev === 'lifecycle' ? null : prev));
  };
  const handleRemoveSyncPill = () => {
    setSyncStatusFilter('all');
    setAddedPillKeys((prev) => prev.filter((k) => k !== 'sync'));
    setOpenPillKey((prev) => (prev === 'sync' ? null : prev));
  };

  const hasActiveFilters =
    !!searchQuery ||
    (isStoreScoped ? storeBrandFilters.length > 0 : !!brandFilter) ||
    !!categoryFilter ||
    catalogStatus !== 'active' ||
    syncStatusFilter !== 'all' ||
    (addedFilterEnabled && !!addedFilter) ||
    effectiveMatchFilter !== 'all' ||
    effectiveLifecycleFilter !== 'all';

  const handleClearAllFilters = () => {
    clearFilters();
    setAddedPillKeys([]);
    setSyncStatusFilter('all');
    setAddedFilter('');
    setMatchFilter('all');
    setLifecycleFilter('all');
    setDiscontinuedInSyncOnly(false);
    setActiveViewName(null);
    closeAllPillPopovers();
  };

  // --- Saved views (per-user, localStorage — see utils/useSavedViews.ts) ---
  const { views: savedViewsRaw, save: saveSavedViewEntry, remove: removeSavedViewEntry } = useSavedViews(userProfile?.uid);

  // Deleting the currently-applied view must un-light the Views button —
  // otherwise it stays "active" for a view that no longer exists.
  const handleRemoveSavedView = (id: string) => {
    const removed = savedViewsRaw.find((v) => v.id === id);
    if (removed && removed.name === activeViewName) {
      setActiveViewName(null);
    }
    removeSavedViewEntry(id);
  };

  const buildCurrentSavedFilters = (): ProductBrowserSavedFilters => ({
    productType: productTypeFilter,
    search: searchQuery,
    brand: brandFilter,
    storeBrands: storeBrandFilters,
    category: categoryFilter,
    catalogStatus,
    syncStatus: syncStatusFilter,
    ...(addedFilterEnabled && addedFilter ? { added: addedFilter } : {}),
    ...(matchFilterEnabled && matchFilter !== 'all' ? { match: matchFilter } : {}),
    ...(lifecycleEnabled && lifecycleFilter !== 'all' ? { lifecycle: lifecycleFilter } : {}),
    pillOrder: addedPillKeys,
  });

  const summarizeSavedFilters = (f: ProductBrowserSavedFilters): string => {
    const bits: string[] = [];
    if (f.productType !== 'all') {
      bits.push(productTypeTabs.find((t) => t.value === f.productType)?.label || f.productType);
    }
    const brandBits = isStoreScoped ? f.storeBrands : (f.brand ? [f.brand] : []);
    if (brandBits.length === 1) bits.push(brandBits[0]);
    else if (brandBits.length > 1) bits.push(`${brandBits.length} brands`);
    if (f.category) bits.push(f.category);
    if (addedFilterEnabled && f.added) bits.push(`${addedPillField.label}: ${addedOptionLabel(f.added)}`);
    if (f.syncStatus !== 'all') bits.push(f.syncStatus === 'sync_issues' ? 'Sync issues' : f.syncStatus === 'in_sync' ? 'In sync' : 'Not in sync');
    if (matchFilterEnabled && f.match && f.match !== 'all') bits.push(matchOptionLabel(f.match));
    if (lifecycleEnabled && f.lifecycle && f.lifecycle !== 'all') bits.push(lifecycleOptionLabel(f.lifecycle));
    if (f.catalogStatus !== 'active') bits.push(f.catalogStatus === 'archived' ? 'Archived' : 'All statuses');
    if (f.search) bits.push(`"${f.search}"`);
    return bits.length ? bits.join(' · ') : 'No filters';
  };

  const savedViewsWithSummary = savedViewsRaw.map((v) => ({ ...v, summary: summarizeSavedFilters(v.filters) }));
  const currentSavedFilters = buildCurrentSavedFilters();
  const currentFilterSummary = summarizeSavedFilters(currentSavedFilters);

  // Derived, not stored: the Views button only stays "lit" while the current
  // filters still match the applied view exactly. Editing any filter after
  // applying a view un-lights it automatically instead of staying stuck on a
  // view that no longer reflects what's shown.
  const activeSavedView = activeViewName ? savedViewsRaw.find((v) => v.name === activeViewName) : undefined;
  const viewsButtonActiveName =
    activeSavedView && sameSavedFilters(activeSavedView.filters, currentSavedFilters)
      ? activeViewName
      : null;

  const applySavedView = (view: ProductBrowserSavedView) => {
    const f = view.filters;
    setProductTypeFilter(f.productType);
    setSearchQuery(f.search);
    if (isStoreScoped) setStoreBrandFilters(f.storeBrands);
    else setBrandFilter(f.brand);
    setCategoryFilter(f.category);
    setCatalogStatus(f.catalogStatus);
    setSyncStatusFilter(f.syncStatus === 'sync_issues' && !syncIssuesEnabled ? 'all' : f.syncStatus);
    setAddedFilter(f.added ?? '');
    setMatchFilter(f.match ?? 'all');
    setLifecycleFilter(f.lifecycle ?? 'all');
    setDiscontinuedInSyncOnly(false);
    setAddedPillKeys(
      f.pillOrder.filter((k): k is ProductPillKey => k === 'category' || k === 'sync' || k === 'added' || k === 'match' || k === 'lifecycle')
    );
    setActiveViewName(view.name);
    setPage(1);
    closeAllPillPopovers();
  };

  const handleSaveCurrentView = (name: string) => {
    saveSavedViewEntry(name, buildCurrentSavedFilters());
    setActiveViewName(name);
  };

  const currentTabTotal = productTypeCounts.get(productTypeFilter);
  // The type tabs only count products with a type, so they can fall short of "All".
  // Name the difference instead of leaving three tabs that do not add up.
  const uncategorisedCount = (() => {
    const all = productTypeCounts.get('all');
    if (all == null) return 0;
    const typed = (['camera', 'lens', 'accessory'] as const).reduce((sum, type) => sum + (productTypeCounts.get(type) ?? 0), 0);
    return Math.max(0, all - typed);
  })();
  /** What the Status pick means for every number on this page: the dashboard's total counts every status. */
  const statusScopeLabel =
    catalogStatus === 'archived' ? 'archived and discontinued' : catalogStatus === 'all' ? 'active, archived and discontinued' : 'active';

  const filteredCameras = useMemo(() => {
    const raw = adminPreReleaseGrid
      ? filterPreReleaseRows(adminPreReleaseProducts || [], {
          search: debouncedSearch.trim(),
          brand: effectiveBrandFilter || undefined,
          category: categoryFilter || undefined,
          productType,
          catalogStatus,
          sort,
        })
      : camerasData?.data;
    let data = Array.isArray(raw) ? raw : [];
    data = data.filter((c): c is NonNullable<typeof c> => c != null && (c as any).id != null);
    if (adminLifecycleMode && !adminPreReleaseGrid) data = attachLifecycle(data, adminLifecycleById);

    if (isStoreScoped) {
      // Narrow a widened (client-paginated) request back down: Active means active
      // plus the discontinued rows this store still syncs. A server-cut page
      // already follows that rule, and narrowing it again would hide those rows
      // until the sync selection has loaded.
      if (!storeGridRequest.includeSyncedDiscontinued) {
        data = filterStoreCatalogRows(data, catalogStatus, syncSelectedIds);
      }
      if (discontinuedInSyncOnly) {
        data = data.filter((camera) => isDiscontinuedInSync(camera, syncSelectedIds));
      }
    }

    data = filterStoreSyncRows(data, syncStatusFilter === 'sync_issues' && !syncIssuesEnabled ? 'all' : syncStatusFilter,
      syncSelectedIds, syncIssueIds);

    if (effectiveMatchFilter !== 'all') {
      // Until the matched ids arrive nothing can be placed, so show nothing rather than everything.
      data = crosswalkMatchedIds
        ? applyMatchFilter(data, effectiveMatchFilter, crosswalkMatchedIds, syncSelectedIds)
        : [];
    }

    data = applyLifecycleFilter(data, effectiveLifecycleFilter);

    return data;
  }, [camerasData?.data, syncStatusFilter, syncSelectedIds, syncIssueIds, syncIssuesEnabled, isStoreScoped, catalogStatus, discontinuedInSyncOnly, effectiveMatchFilter, crosswalkMatchedIds, effectiveLifecycleFilter, storeGridRequest.includeSyncedDiscontinued, adminPreReleaseGrid, adminPreReleaseProducts, adminLifecycleMode, adminLifecycleById, debouncedSearch, effectiveBrandFilter, categoryFilter, productType, sort]);

  /**
   * Counted from the rows the current filters actually returned — not the store-wide
   * universe — so the banner never advertises discontinued results that a brand or
   * search filter excluded. Read off the raw response so the number holds steady
   * while the drill-down is on.
   */
  const discontinuedInSyncVisible = useMemo(() => {
    if (!storeWidensForSyncedDiscontinued) return 0;
    // A server-cut page reports how many it merged in across the whole filtered
    // result, since the rows on this page are only a slice of it.
    if (storeGridRequest.includeSyncedDiscontinued) return camerasData?.pagination?.discontinuedInSync ?? 0;
    const raw = Array.isArray(camerasData?.data) ? camerasData!.data : [];
    return countDiscontinuedInSyncRows(
      raw.filter((camera): camera is NonNullable<typeof camera> => !!camera),
      syncSelectedIds,
    );
  }, [storeWidensForSyncedDiscontinued, storeGridRequest.includeSyncedDiscontinued, camerasData?.data, camerasData?.pagination?.discontinuedInSync, syncSelectedIds]);

  const showDiscontinuedInSyncBanner = discontinuedInSyncVisible > 0;

  /** The grid holds its whole result and pages it here: a store's client-side filters, or the admin's pre-release views. */
  const clientPaginatedGrid = shouldUseFullStoreSyncDataset || adminPreReleaseGrid;

  const paginatedCameras = useMemo(() => {
    if (!clientPaginatedGrid) return filteredCameras;
    const start = (page - 1) * limit;
    return filteredCameras.slice(start, start + limit);
  }, [filteredCameras, clientPaginatedGrid, page, limit]);

  const pricingLookupItems = useMemo(
    () =>
      paginatedCameras
        .filter((camera) => !!(camera.model || camera.sku))
        .map((camera) => ({
          model: camera.model || camera.sku || '',
          productId: camera.id,
          brand: camera.brand,
          sku: camera.sku,
          mpn: camera.mpn,
        })),
    [paginatedCameras]
  );

  const pricingBatchQuery = useQuery({
    queryKey: [
      'pricing-batch',
      pricingLookupItems.map(
        (item) => `${toPricingLookupKey(item.model, item.productId)}::${item.sku || ''}::${item.mpn || ''}::${item.brand || ''}`
      ),
    ],
    queryFn: () => apiService.getPricingByModels(pricingLookupItems),
    enabled: pricingLookupItems.length > 0,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });

  const pricingByProductIdRef = useRef<Map<string, ProductPricingData>>(new Map());

  // Create a map keyed by product id for quick lookup.
  const pricingMap = useMemo(() => {
    const batch = pricingBatchQuery.data || {};

    paginatedCameras.forEach((camera) => {
      const lookupModel = camera.model || camera.sku;
      if (!lookupModel) return;
      const pricingData = toListingPricingData(batch[toPricingLookupKey(lookupModel, camera.id)]);
      if (pricingData) {
        pricingByProductIdRef.current.set(camera.id, pricingData);
      }
    });

    const map = new Map<string, ProductPricingData>();
    paginatedCameras.forEach((camera) => {
      const lookupModel = camera.model || camera.sku;
      const pricingData = lookupModel
        ? toListingPricingData(batch[toPricingLookupKey(lookupModel, camera.id)])
        : undefined;
      if (pricingData) {
        map.set(camera.id, pricingData);
        return;
      }
      const cached = pricingByProductIdRef.current.get(camera.id);
      if (cached) map.set(camera.id, cached);
    });

    return map;
  }, [paginatedCameras, pricingBatchQuery.data]);

  // The store's own products on this page carry no inline price (flag `store_browser_all_synced`).
  const ownPriceIds = useMemo(
    () => (allSyncedEnabled ? paginatedCameras.filter((camera) => String(camera.status) === 'placeholder').map((camera) => camera.id) : []),
    [allSyncedEnabled, paginatedCameras]
  );
  const { data: ownPrices } = useQuery({
    queryKey: ['own-product-prices', ownPriceIds],
    queryFn: () => apiService.getOwnProductPrices(ownPriceIds),
    enabled: ownPriceIds.length > 0,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  // Merge cameras with pricing data
  const camerasWithPricing: CameraWithPricing[] = useMemo(() => {
    return paginatedCameras.map((camera) => ({
      ...withOwnProductPrice(camera, ownPrices?.[camera.id]),
      pricingData: pricingMap.get(camera.id),
    }));
  }, [paginatedCameras, pricingMap, ownPrices]);
  const visibleProductIds = useMemo(() => camerasWithPricing.map((camera) => camera.id), [camerasWithPricing]);

  // A server-cut Active page carries the synced discontinued rows itself, so a
  // selection change has to refetch it: the rows and the banner count come from
  // the server, and the query key holds no selection.
  const invalidateServerCutPage = () => {
    // With `store_browser_all_synced` on, the selection effect above does this once the selection is read back.
    if (storeGridRequest.includeSyncedDiscontinued && !allSyncedEnabled) {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  };
  const onAddedToSync = () => {
    queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
    queryClient.invalidateQueries({ queryKey: ['sync-selection'] });
    queryClient.invalidateQueries({ queryKey: ['storeCatalogCounts'] });
    invalidateServerCutPage();
    setSyncCapError(null);
    showToast(nextSyncCopy ? `Products added to sync selection. ${nextSyncCopy.detail}` : 'Products added to sync selection.', 'success');
  };
  const onAddToSyncError = (error: unknown) => {
    // The plan cap gets its own inline notice (limit + link to Billing);
    // anything else keeps the generic toast, now with the server's reason.
    if (error instanceof SyncSelectionCapError) {
      setSyncCapError(error);
      return;
    }
    showToast(error instanceof Error && error.message ? error.message : 'Failed to add products to sync.', 'error');
  };
  const addToSyncMutation = useMutation({
    mutationFn: (productIds: string[]) =>
      apiService.addStoreProductsToSync(productIds, selectionIntegrationId),
    onSuccess: () => {
      onAddedToSync();
      setSelectedProductIds(new Set());
    },
    onError: onAddToSyncError,
  });
  // Adds a related product from the product details dialog (Compatibility tab):
  // same write and feedback, but the products checked in the grid stay checked.
  const addRelatedToSyncMutation = useMutation({
    mutationFn: (productIds: string[]) =>
      apiService.addStoreProductsToSync(productIds, selectionIntegrationId),
    onSuccess: onAddedToSync,
    onError: onAddToSyncError,
  });

  const removeFromSyncMutation = useMutation({
    mutationFn: (productIds: string[]) =>
      apiService.removeStoreProductsFromSync(productIds, selectionIntegrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      queryClient.invalidateQueries({ queryKey: ['sync-selection'] });
      queryClient.invalidateQueries({ queryKey: ['storeCatalogCounts'] });
      invalidateServerCutPage();
      showToast('Products removed from sync selection.', 'success');
      setSelectedProductIds(new Set());
    },
    onError: () => {
      showToast('Failed to remove products from sync.', 'error');
    },
  });

  const totalSelected = selectedProductIds.size;
  const selectedInSyncCount = useMemo(
    () => Array.from(selectedProductIds).filter((id) => syncSelectedIds.has(id)).length,
    [selectedProductIds, syncSelectedIds]
  );

  const handleToggleProductSelection = (productId: string) => {
    setAllFilteredSelected(false);
    setSelectedProductIds((previous) => {
      const next = new Set(previous);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const handleSelectVisibleProducts = () => {
    setAllFilteredSelected(false);
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      visibleProductIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSelectAllFiltered = async () => {
    setSelectAllFilteredLoading(true);
    try {
      // When the Active view widens for discontinued-in-sync (or other client-side
      // store filters), select from that same set — the filtered-ids API still uses
      // catalogStatus: 'active' and would drop the discontinued rows on screen.
      if (clientPaginatedGrid) {
        setSelectedProductIds(new Set(filteredCameras.map((camera) => camera.id)));
        setAllFilteredSelected(true);
        return;
      }
      const ids = await apiService.getFilteredProductIds({
        search: debouncedSearch || undefined,
        category: categoryFilter || undefined,
        productType: productType || undefined,
        brand: effectiveBrandFilter || undefined,
        catalogStatus,
        addedSince,
        includeSyncedDiscontinued: storeGridRequest.includeSyncedDiscontinued,
        integrationId: storeGridRequest.includeSyncedDiscontinued ? selectionIntegrationId : rowsIntegrationId,
      });
      setSelectedProductIds(new Set(ids));
      setAllFilteredSelected(true);
    } catch {
      showToast('Failed to select all filtered products.', 'error');
    } finally {
      setSelectAllFilteredLoading(false);
    }
  };

  const handleClearProductSelection = () => {
    setSelectedProductIds(new Set());
    setAllFilteredSelected(false);
  };

  const handleAddSelectedToSync = () => {
    if (totalSelected === 0) return;
    addToSyncMutation.mutate(Array.from(selectedProductIds));
  };

  const handleRemoveSelectedFromSync = () => {
    if (totalSelected === 0) return;
    removeFromSyncMutation.mutate(Array.from(selectedProductIds));
  };

  const scopedTotal = clientPaginatedGrid
    ? filteredCameras.length
    : (camerasData?.pagination?.total ?? filteredCameras.length);
  const showScopedEmptyState = isStoreScoped && scopedTotal === 0
    && !(syncStatusFilter === 'sync_issues' && syncIssuesError);

  const totalForPagination = clientPaginatedGrid
    ? filteredCameras.length
    : (camerasData?.pagination?.total ?? filteredCameras.length);

  const showProductSkeletons = adminPreReleaseGrid
    ? adminPreReleaseLoading
    : awaitingStoreCounts || isLoading || (isFetching && camerasData == null)
      || (syncStatusFilter === 'sync_issues' && syncIssuesEnabled && syncIssuesLoading);
  // Search and other refetches keep the previous rows (placeholderData), so
  // without this the grid looks idle for the whole request.
  const resultsRefreshing = isFetching && !showProductSkeletons;
  const appliedSearch = debouncedSearch.trim();
  const typedSearch = searchQuery.trim();
  // The search whose rows are on screen. Updated only while nothing is in
  // flight, so the render that starts a search still sees the previous query.
  // A page, filter, or sort refetch does not change it.
  const settledSearchRef = useRef(appliedSearch);
  if (!resultsRefreshing) settledSearchRef.current = appliedSearch;
  const searchBusy =
    typedSearch.length > 0 &&
    (typedSearch !== appliedSearch || (resultsRefreshing && appliedSearch !== settledSearchRef.current));

  // Clear "all filtered" flag when filters change, but keep explicit selections
  useEffect(() => {
    setAllFilteredSelected(false);
  }, [debouncedSearch, categoryFilter, productType, effectiveBrandFilter, catalogStatus, addedFilter, effectiveMatchFilter, effectiveLifecycleFilter]);

  // Reset page to 1 when filters or limit change
  useEffect(() => {
    setPage(1);
  }, [effectiveBrandFilter, categoryFilter, searchQuery, productTypeFilter, limit, sort, syncStatusFilter, catalogStatus, discontinuedInSyncOnly, addedFilter, effectiveMatchFilter, effectiveLifecycleFilter]);

  // The drill-down only exists while the banner does. Without this, changing the
  // Status pick or narrowing to a brand with no discontinued rows would strand the
  // user on an empty grid with no way back.
  useEffect(() => {
    if (!discontinuedInSyncOnly || showProductSkeletons) return;
    if (!showDiscontinuedInSyncBanner) setDiscontinuedInSyncOnly(false);
  }, [discontinuedInSyncOnly, showDiscontinuedInSyncBanner, showProductSkeletons]);

  // Clear category filter when brand changes if current category isn't valid for new brand
  useEffect(() => {
    if (categoryBrandFilter && categoryFilter) {
      // Check if the current category is valid for the new brand
      if (brandCategories && !brandCategories.includes(categoryFilter)) {
        setCategoryFilter('');
      }
    }
  }, [categoryBrandFilter, brandCategories, categoryFilter]);

  // Clear brand filter when category changes (if we want to allow this)
  // useEffect(() => {
  //   if (categoryFilter && brandFilter) {
  //     // Optionally clear brand filter when category changes
  //     // setBrandFilter('');
  //   }
  // }, [categoryFilter]);

  const handleCameraClick = (camera: CameraWithPricing) => {
    setSelectedCamera(camera);
    if (permalinksOn) {
      // The row is already on screen, so it fills the shared entry and the dialog opens at once.
      permalink.seed(camera.id, camera);
      permalinkNav.open(camera.id);
      return;
    }
    setDialogOpen(true);
  };

  // Switches the open product (a variant, a linked product) and, with direct links on, the address with it.
  const switchOpenProduct = (product: CameraWithPricing) => {
    setSelectedCamera(product);
    if (permalinksOn) {
      permalink.seed(product.id, product);
      permalinkNav.open(product.id, { replace: true });
    }
  };

  // The address decides what is open: a shared link or refresh fills the product from the one by-id read,
  // and leaving the address (Back, close) clears it. Only a change of route clears, so a click that has
  // set the product but not yet reached the address is left alone.
  const previousRouteProductId = useRef('');
  useEffect(() => {
    if (!permalinksOn) return;
    const previous = previousRouteProductId.current;
    previousRouteProductId.current = routeProductId;
    if (!routeProductId) {
      if (previous) {
        setSelectedCamera(null);
        setVariantSwitchLoadingSku(null);
      }
      return;
    }
    const item = permalink.item;
    if (item && item.id === routeProductId && selectedCameraRef.current?.id !== routeProductId) {
      setSelectedCamera(item);
    }
  }, [permalinksOn, routeProductId, permalink.item]);

  // The older ?productId= link keeps working: it moves to the address.
  const legacyProductIdParam = (searchParams.get('productId') || '').trim();
  useEffect(() => {
    if (!permalinksOn || routeProductId || !legacyProductIdParam) return;
    const rest = new URLSearchParams(searchParams);
    rest.delete('productId');
    const search = rest.toString();
    navigate(
      { pathname: `/app/products/${encodeURIComponent(legacyProductIdParam)}`, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [permalinksOn, routeProductId, legacyProductIdParam, searchParams, navigate]);

  // Opens one product by id in the details dialog: a submitted or privately uploaded
  // product of this store reads through the same product route (MOT-695).
  const openProductById = useCallback(async (productId: string) => {
    try {
      const product = await apiService.getCameraById(productId);
      if (!product?.id) return;
      setSelectedCamera(product as CameraWithPricing);
      if (permalinksOn) {
        permalink.seed(product.id, product as CameraWithPricing);
        permalinkNav.open(product.id);
        return;
      }
      setDialogOpen(true);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not open the product', 'error');
    }
  }, [showToast, permalinksOn, permalink.seed, permalinkNav.open]);

  const deepLinkedProductId = (searchParams.get('productId') || '').trim();
  useEffect(() => {
    // With direct links on, the redirect above carries the id to the address instead.
    if (permalinkFlagLoading || permalinksOn) return;
    if (!deepLinkedProductId || openedDeepLinkRef.current === deepLinkedProductId) return;
    let cancelled = false;
    (async () => {
      try {
        const product = await apiService.getCameraById(deepLinkedProductId);
        if (cancelled || !product?.id) return;
        openedDeepLinkRef.current = deepLinkedProductId;
        setSelectedCamera(product as CameraWithPricing);
        setDialogOpen(true);
      } catch {
        if (!cancelled) {
          setSearchQuery((prev) => prev || deepLinkedProductId);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deepLinkedProductId, permalinkFlagLoading, permalinksOn]);

  const handleVariantOptionClick = async (variant: {
    sku?: string;
    upc?: string;
    optionValue?: string;
    title?: string;
    name?: string;
  }) => {
    const targetSku = (variant.sku || '').trim();
    const label = variantDisplayLabel(variant);
    if (!targetSku || !selectedCamera) return;
    if (
      targetSku === selectedCamera.sku ||
      (variant.upc && variant.upc === selectedCamera.upc)
    ) {
      return;
    }

    setVariantSwitchLoadingSku(targetSku);
    try {
      const fromPage = camerasWithPricing.find(
        (c) =>
          c.sku === targetSku ||
          (variant.upc && c.upc === variant.upc)
      );
      if (fromPage) {
        switchOpenProduct(fromPage);
        return;
      }

      const result = await apiService.searchProducts(
        targetSku,
        10,
        0,
        undefined,
        'all',
        selectedCamera.brand || undefined,
      );
      const products = result?.data || [];
      const brandMatch = (selectedCamera.brand || '').toLowerCase();
      const match =
        products.find(
          (p) =>
            p.sku?.toLowerCase() === targetSku.toLowerCase() &&
            (!brandMatch || (p.brand || '').toLowerCase() === brandMatch)
        ) ||
        products.find((p) => p.sku?.toLowerCase() === targetSku.toLowerCase()) ||
        (variant.upc
          ? products.find((p) => p.upc === variant.upc)
          : undefined);

      if (match) {
        switchOpenProduct(match as CameraWithPricing);
      } else {
        showToast(`${label || targetSku} is listed as an option but is not in the catalog yet.`, 'info');
      }
    } catch {
      showToast(`Failed to open option ${label || targetSku}.`, 'error');
    } finally {
      setVariantSwitchLoadingSku(null);
    }
  };

  const openLinkedCatalogProduct = (product: Product) => {
    if (!selectedCamera || product.id === selectedCamera.id) return;
    switchOpenProduct(product as CameraWithPricing);
  };

  const handleCloseDialog = () => {
    if (permalinksOn && routeProductId) {
      // Back to the list with its filters; the route effect clears the product.
      permalinkNav.close();
      return;
    }
    setDialogOpen(false);
    setSelectedCamera(null);
    setVariantSwitchLoadingSku(null);
  };

  // With direct links on, the address says whether the dialog is open.
  const dialogProduct: CameraWithPricing | null = permalinksOn
    ? routeProductId && !permalink.notFound
      ? selectedCamera?.id === routeProductId
        ? selectedCamera
        : permalink.item ?? null
      : null
    : selectedCamera;

  const handleImageClick = (camera: Product, imageIndex: number = 0) => {
    setGalleryImages((camera.images ?? []).map((img) => getImageUrl(img)).filter((u): u is string => !!u));
    setGalleryTitle(camera.name || camera.model);
    setGalleryInitialIndex(imageIndex);
    setImageGalleryOpen(true);
  };

  const handleOpenGalleryFromDialog = (images: string[], title: string, index: number) => {
    setGalleryImages(images);
    setGalleryTitle(title);
    setGalleryInitialIndex(index);
    setImageGalleryOpen(true);
  };

  const handleCloseImageGallery = () => {
    setImageGalleryOpen(false);
    setGalleryImages([]);
    setGalleryTitle('');
    setGalleryInitialIndex(0);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setBrandFilter('');
    setStoreBrandFilters([]);
    setCategoryFilter('');
    setProductTypeFilter('all');
    setLimit(25);
    setPage(1);
    setCatalogStatus('active');
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const { makeApiCall, getAuthHeaders } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (brandFilter) params.set('brand', brandFilter);
      if (productTypeFilter !== 'all') params.set('productType', productTypeFilter);
      if (catalogStatus !== 'active') params.set('status', catalogStatus);
      const url = `/products/export/csv${params.toString() ? `?${params}` : ''}`;
      const response = await makeApiCall(url, { headers });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `products-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('CSV export failed:', err);
      showToast('CSV export failed. Please try again.', 'error');
    } finally {
      setExportingCsv(false);
    }
  };

  // --- Pagination-bar display values (shared by the mirrored top/bottom bars) ---
  const pageCount = totalForPagination ? Math.max(1, Math.ceil(totalForPagination / limit)) : 1;
  const rangeStart = totalForPagination === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, totalForPagination);
  const rangeLabel = searchBusy
    ? tx('productBrowser.search.searching')
    : totalForPagination > 0
      ? `${numberFormat(rangeStart)}–${numberFormat(rangeEnd)} of ${numberFormat(totalForPagination)}`
      : '0 of 0';

  // Opens the comparison view with the ticked products (the button is disabled outside 2 to 4).
  const handleCompareSelected = () => {
    if (!canCompareSelection(totalSelected, allFilteredSelected)) return;
    navigate(buildCompareUrl(selectedProductIds));
  };

  const handleSelectAllOrPageOnly = () => {
    if (allFilteredSelected) {
      handleClearProductSelection();
      handleSelectVisibleProducts();
    } else {
      handleSelectAllFiltered();
    }
  };

  // Sync actions drop into the pagination bar's already-reserved middle space —
  // nothing shifts when a selection starts or ends.
  const selectionMiddleContent =
    totalSelected > 0 ? (
      <>
        <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'primary.dark', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {numberFormat(totalSelected)} selected
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSelectAllOrPageOnly}
          disabled={selectAllFilteredLoading}
          startIcon={selectAllFilteredLoading ? <CircularProgress size={12} /> : undefined}
          sx={{ height: 30, whiteSpace: 'nowrap' }}
        >
          {allFilteredSelected ? 'Select page only' : `Select all ${numberFormat(totalForPagination)} filtered`}
        </Button>
        <Divider orientation="vertical" flexItem sx={{ height: 20 }} />
        <Button
          size="small"
          variant="contained"
          startIcon={addToSyncMutation.isPending ? <CircularProgress size={12} color="inherit" /> : <PlaylistAdd sx={{ fontSize: 15 }} />}
          onClick={handleAddSelectedToSync}
          disabled={addToSyncMutation.isPending}
          sx={{ height: 30, whiteSpace: 'nowrap' }}
        >
          {`Add ${numberFormat(totalSelected)} to sync`}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={removeFromSyncMutation.isPending ? <CircularProgress size={12} /> : <PlaylistRemove sx={{ fontSize: 15 }} />}
          onClick={handleRemoveSelectedFromSync}
          disabled={removeFromSyncMutation.isPending}
          sx={{ height: 30, whiteSpace: 'nowrap' }}
        >
          Remove from sync
        </Button>
        <CompareSelectionButton
          enabled={compareEnabled}
          selectedCount={totalSelected}
          allFilteredSelected={allFilteredSelected}
          onOpen={handleCompareSelected}
        />
        <Divider orientation="vertical" flexItem sx={{ height: 20 }} />
        <Typography component="span" sx={{ fontSize: '0.75rem', color: 'primary.main', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {numberFormat(selectedInSyncCount)} in sync
        </Typography>
        <Button size="small" onClick={handleClearProductSelection} sx={{ minWidth: 0, px: 0, fontSize: '0.8125rem', color: 'primary.main', whiteSpace: 'nowrap' }}>
          Clear
        </Button>
      </>
    ) : null;

  // With nothing selected the top bar's middle space says when the selection next pushes.
  const nextSyncMiddleContent =
    isStoreScoped && nextSyncCopy ? (
      <Tooltip title={nextSyncCopy.detail} arrow>
        <Typography
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', cursor: 'default' }}
        >
          <CloudSync sx={{ fontSize: 14 }} />
          {nextSyncCopy.short.charAt(0).toUpperCase() + nextSyncCopy.short.slice(1)}
        </Typography>
      </Tooltip>
    ) : null;

  // One sentence of page context: what the page is, plus the scope notice that used to be a
  // second banner repeating it.
  const vendorScopeNotice =
    isVendor && !isStoreScoped
      ? isDemoMode
        ? `Demo mode: viewing ${vendorBrandName} products.`
        : vendorCanBrowseOtherBrands
          ? 'As a distributor, you can browse brands beyond your own catalog.'
          : `Viewing products from ${vendorBrandName} only.`
      : null;
  const pageDescription = [
    'Browse and search the product database. Select a product for full details.',
    vendorScopeNotice,
    isPlatformAdmin && !isStoreScoped ? 'Use "Open in Product Manager" on a product for canonical edits.' : null,
  ]
    .filter(Boolean)
    .join(' ');

  // The admin's pre-release views read their own query (the catalog query is off there), so its
  // failure is shown here too, never as an empty grid; leaving the view returns to the catalog.
  if (adminPreReleaseGrid && adminPreReleaseError) {
    return (
      <Alert
        severity="error"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button color="inherit" size="small" onClick={() => refetchAdminPreRelease()}>
              Retry
            </Button>
            <Button color="inherit" size="small" onClick={() => setLifecycleFilter('all')}>
              Show all products
            </Button>
          </Box>
        }
      >
        Failed to load unreleased products.
      </Alert>
    );
  }

  if (error) {
    return (
      <>
        <Alert severity="error">
          Failed to load products. Please try again later.
        </Alert>
      </>
    );
  }

  return (
    <>
      <Box id="camera-browser-box" sx={{ width: '100%', p: 0, m: 0 }}>
        {/* Vendor brand filter info banner. Stores are scoped to brandsSold, not this. */}
        {isVendor && !isStoreScoped && (
          <Alert 
            severity="info" 
            sx={{ display: { xs: 'flex', sm: 'none' }, mx: { xs: 1.5, sm: 3 }, mt: 2, mb: 0 }}
            icon={vendorCanBrowseOtherBrands ? <InfoIcon /> : <Lock />}
          >
            {isDemoMode 
              ? `Demo Mode: Viewing ${vendorBrandName} products`
              : vendorCanBrowseOtherBrands
                ? 'As a distributor, you can browse brands beyond your own catalog.'
                : `Viewing products from ${vendorBrandName} only`
            }
          </Alert>
        )}
        
        {syncCapError && <SyncCapNotice error={syncCapError} onDismiss={() => setSyncCapError(null)} />}
        {syncStatusFilter === 'sync_issues' && syncIssuesEnabled && syncIssuesError && (
          <Alert severity="error" sx={{ mx: { xs: 1.5, sm: 3 }, mt: 2 }}
            action={<Button color="inherit" size="small" onClick={() => void refetchSyncIssues()}>Retry</Button>}>
            Could not load sync issues. Retry to see the affected products.
          </Alert>
        )}

        {isStoreScoped && selectionIntegrationId && (
          <Alert severity="info" sx={{ mx: { xs: 1.5, sm: 3 }, mt: 2 }}>
            You are choosing the products for one integration. Adding or removing products here
            changes that integration's selection only, not the rest of your store.
          </Alert>
        )}

        <Box sx={{ display: { xs: 'none', sm: 'block' }, px: 3, pt: 2 }}>
          <PageHeader
            title="Product Browser"
            description={pageDescription}
          />
        </Box>

        {permalinksOn && permalink.notFound && (
          <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: 2 }}>
            <PermalinkNotFound what="product" onBack={permalinkNav.close} />
          </Box>
        )}
        {permalinksOn && permalink.loadFailed && (
          <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: 2 }}>
            <PermalinkLoadFailed what="product" onRetry={() => void permalink.refetch()} />
          </Box>
        )}

        {/* Role-specific Actions (non-store) */}
        {!isStoreScoped && isVendor && (
          <Paper sx={{ p: 2, mb: 3, mx: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              {isVendor && (
                <>
                  <Button size="small" variant="outlined" onClick={() => navigate('/app/vendor-map-management')}>MAP Management</Button>
                  <Button size="small" variant="outlined" onClick={() => navigate('/app/price-history')}>Price History</Button>
                  <Button size="small" variant="text" onClick={() => navigate('/app/map-pricing-data')}>MAP Pricing Data</Button>
                </>
              )}
            </Box>
          </Paper>
        )}

            {/* Product Browser: tabs + view controls, search + filter pills, results */}
            <Paper sx={{ mx: { xs: 1.5, sm: 3 }, mb: 3, overflow: 'hidden' }}>
              {/* Row 1: Product type tabs + view controls (Export CSV / Cards-List) */}
              <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, px: { xs: 1.25, sm: 2.5 }, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', flexWrap: 'wrap' }}>
                <ProductTypeTabs
                  tabs={productTypeTabs}
                  value={productTypeFilter}
                  countsLoading={tabCountsLoading}
                  trailingNote={
                    uncategorisedCount > 0 && productTypeFilter === 'all'
                      ? `${numberFormat(uncategorisedCount)} uncategorised`
                      : undefined
                  }
                  onChange={(value) => {
                    setProductTypeFilter(value);
                    setCategoryFilter('');
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <Tooltip title="Export filtered products as CSV">
                    <Button
                      variant="outlined"
                      size="small"
                      aria-label={exportingCsv ? 'Exporting CSV' : 'Export CSV'}
                      startIcon={exportingCsv ? <CircularProgress size={14} /> : <Download sx={{ fontSize: 16 }} />}
                      onClick={handleExportCsv}
                      disabled={exportingCsv}
                      sx={{
                        height: 34,
                        minWidth: { xs: 34, sm: 'auto' },
                        px: { xs: 1, sm: 1.5 },
                        whiteSpace: 'nowrap',
                        color: 'text.secondary',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 } },
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {exportingCsv ? 'Exporting…' : 'Export CSV'}
                      </Box>
                    </Button>
                  </Tooltip>
                  <Box sx={{ display: 'flex', p: 0.25, bgcolor: 'grey.100', borderRadius: '8px', border: '1px solid', borderColor: 'divider', gap: 0.25 }}>
                    <Button
                      size="small"
                      onClick={() => setViewMode('card')}
                      aria-label="Card view"
                      startIcon={<ViewModule sx={{ fontSize: 15 }} />}
                      sx={{
                        height: 28,
                        minWidth: 0,
                        px: { xs: 0.75, sm: 1.25 },
                        fontSize: '0.78125rem',
                        bgcolor: viewMode === 'card' ? 'background.paper' : 'transparent',
                        color: viewMode === 'card' ? 'primary.dark' : 'text.secondary',
                        boxShadow: viewMode === 'card' ? '0 1px 2px rgba(15,23,42,0.12)' : 'none',
                        '&:hover': { bgcolor: viewMode === 'card' ? 'background.paper' : 'action.hover' },
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 } },
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Cards</Box>
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setViewMode('list')}
                      aria-label="List view"
                      startIcon={<ViewList sx={{ fontSize: 15 }} />}
                      sx={{
                        height: 28,
                        minWidth: 0,
                        px: { xs: 0.75, sm: 1.25 },
                        fontSize: '0.78125rem',
                        bgcolor: viewMode === 'list' ? 'background.paper' : 'transparent',
                        color: viewMode === 'list' ? 'primary.dark' : 'text.secondary',
                        boxShadow: viewMode === 'list' ? '0 1px 2px rgba(15,23,42,0.12)' : 'none',
                        '&:hover': { bgcolor: viewMode === 'list' ? 'background.paper' : 'action.hover' },
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 } },
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>List</Box>
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Row 2: search + filter pills */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 1.25, sm: 2.5 }, py: 1.75, flexWrap: 'wrap' }}>
                <Box
                  component="form"
                  role="search"
                  onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    runSearchNow();
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, flex: '0 0 auto' }}
                >
                  <TextField
                    size="small"
                    placeholder="Search name, model, SKU, MPN or UPC"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    inputProps={{ 'aria-label': 'Search by name, model, SKU, MPN or UPC' }}
                    sx={{ width: { xs: '100%', sm: 320 }, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
                    InputProps={{
                      sx: { height: 38 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    size="small"
                    aria-busy={searchBusy || undefined}
                    sx={{ height: 38, flex: '0 0 auto', minWidth: 118 }}
                    startIcon={searchBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    {searchBusy ? tx('productBrowser.search.searching') : tx('productBrowser.search.button')}
                  </Button>
                </Box>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, height: 24, alignSelf: 'center', mx: 0.5 }} />

                <FilterPill
                  field={brandPillField}
                  options={brandPillOptions}
                  value={brandPillValue}
                  active={brandPillActive}
                  leadingMark={brandLeadingMark}
                  open={openPillKey === 'brand'}
                  onOpen={() => {
                    setOpenPillKey('brand');
                    setAddFilterOpen(false);
                  }}
                  onClose={() => setOpenPillKey((prev) => (prev === 'brand' ? null : prev))}
                  onChange={handleBrandPillChange}
                  onClear={
                    brandPillActive
                      ? () => {
                          handleBrandPillChange(isStoreScoped ? [] : '');
                          setOpenPillKey((prev) => (prev === 'brand' ? null : prev));
                        }
                      : undefined
                  }
                />

                <FilterPill
                  field={statusPillField}
                  options={STATUS_PILL_OPTIONS}
                  value={catalogStatus}
                  active={catalogStatus !== 'active'}
                  optionLabel={statusOptionLabel}
                  open={openPillKey === 'status'}
                  onOpen={() => {
                    setOpenPillKey('status');
                    setAddFilterOpen(false);
                  }}
                  onClose={() => setOpenPillKey((prev) => (prev === 'status' ? null : prev))}
                  onChange={handleStatusPillChange}
                />

                {addedPillKeys.includes('category') && (
                  <FilterPill
                    field={categoryPillField}
                    options={categoryPillOptions}
                    value={categoryFilter}
                    active={!!categoryFilter}
                    hint={categoryHint}
                    open={openPillKey === 'category'}
                    onOpen={() => {
                      setOpenPillKey('category');
                      setAddFilterOpen(false);
                    }}
                    onClose={() => setOpenPillKey((prev) => (prev === 'category' ? null : prev))}
                    onChange={handleCategoryPillChange}
                    onRemove={handleRemoveCategoryPill}
                  />
                )}

                {isStoreScoped && addedPillKeys.includes('sync') && (
                  <FilterPill
                    field={syncPillField}
                    options={syncPillOptions}
                    value={syncPillValue}
                    active={!!syncPillValue}
                    optionLabel={syncOptionLabel}
                    open={openPillKey === 'sync'}
                    onOpen={() => {
                      setOpenPillKey('sync');
                      setAddFilterOpen(false);
                    }}
                    onClose={() => setOpenPillKey((prev) => (prev === 'sync' ? null : prev))}
                    onChange={handleSyncPillChange}
                    onRemove={handleRemoveSyncPill}
                  />
                )}

                {matchFilterEnabled && addedPillKeys.includes('match') && (
                  <FilterPill
                    field={matchPillField}
                    options={matchPillOptions}
                    value={matchPillValue}
                    active={!!matchPillValue}
                    optionLabel={matchOptionLabel}
                    hint={crosswalkMatchedError ? tx('productBrowser.matchFilter.loadError') : undefined}
                    open={openPillKey === 'match'}
                    onOpen={() => {
                      setOpenPillKey('match');
                      setAddFilterOpen(false);
                    }}
                    onClose={() => setOpenPillKey((prev) => (prev === 'match' ? null : prev))}
                    onChange={handleMatchPillChange}
                    onRemove={handleRemoveMatchPill}
                  />
                )}

                {lifecycleEnabled && addedPillKeys.includes('lifecycle') && (
                  <FilterPill
                    field={lifecyclePillField}
                    options={lifecyclePillOptions}
                    value={lifecyclePillValue}
                    active={!!lifecyclePillValue}
                    optionLabel={lifecycleOptionLabel}
                    open={openPillKey === 'lifecycle'}
                    onOpen={() => {
                      setOpenPillKey('lifecycle');
                      setAddFilterOpen(false);
                    }}
                    onClose={() => setOpenPillKey((prev) => (prev === 'lifecycle' ? null : prev))}
                    onChange={handleLifecyclePillChange}
                    onRemove={handleRemoveLifecyclePill}
                  />
                )}

                {addedFilterEnabled && addedPillKeys.includes('added') && (
                  <FilterPill
                    field={addedPillField}
                    options={addedPillOptions}
                    value={addedFilter}
                    active={!!addedFilter}
                    optionLabel={addedOptionLabel}
                    open={openPillKey === 'added'}
                    onOpen={() => {
                      setOpenPillKey('added');
                      setAddFilterOpen(false);
                    }}
                    onClose={() => setOpenPillKey((prev) => (prev === 'added' ? null : prev))}
                    onChange={handleAddedPillChange}
                    onRemove={handleRemoveAddedPill}
                    menuFooter={
                      <Box sx={{ px: 1.125, pt: 1, pb: 0.75, borderTop: 1, borderColor: 'divider', mt: 0.5 }}>
                        <DateField
                          sx={{ width: '100%' }}
                          label={tx('productBrowser.addedFilter.customDate')}
                          aria-label={tx('productBrowser.addedFilter.customDateAria')}
                          max={localIsoDay()}
                          value={addedFilter.startsWith('since:') ? addedFilter.slice('since:'.length) : ''}
                          onChange={(iso) => {
                            const next = customAddedFilterValue(iso);
                            if (next) setAddedFilter(next);
                          }}
                        />
                      </Box>
                    }
                  />
                )}

                <AddFilterMenu
                  fields={addablePillFields}
                  open={addFilterOpen}
                  onOpen={() => {
                    setAddFilterOpen(true);
                    setOpenPillKey(null);
                  }}
                  onClose={() => setAddFilterOpen(false)}
                  onAdd={handleAddPill}
                />

                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, height: 24, alignSelf: 'center', mx: 0.5 }} />

                <ViewsMenu
                  views={savedViewsWithSummary}
                  activeViewName={viewsButtonActiveName}
                  onApply={applySavedView}
                  onRemove={handleRemoveSavedView}
                  currentSummary={currentFilterSummary}
                  onSave={handleSaveCurrentView}
                />

                {placeholdersEnabled && (
                  <PricingOnlyPlaceholdersToggle open={showPlaceholders} onToggle={() => setShowPlaceholders((prev) => !prev)} />
                )}

                {productUploadEnabled && (
                  <StoreProductUploadToggle open={showProductUpload} onToggle={() => setShowProductUpload((prev) => !prev)} />
                )}

                {productSubmissionsEnabled && (
                  <ProductSubmissionsToggle open={showProductSubmissions} onToggle={() => setShowProductSubmissions((prev) => !prev)} />
                )}

                <Box sx={{ flex: '1 1 auto', minWidth: 12 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={sort}
                      onChange={(e) => setSort(e.target.value as CatalogSort)}
                      inputProps={{ 'aria-label': 'Sort products' }}
                      sx={{ height: 34, fontSize: '0.8125rem' }}
                    >
                      <MenuItem value="name_asc">Sort: Name A–Z</MenuItem>
                      <MenuItem value="name_desc">Sort: Name Z–A</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.125 }}>
                    {searchBusy ? (
                      <Typography role="status" aria-live="polite" sx={{ fontSize: '0.84375rem', color: 'text.secondary' }}>
                        {tx('productBrowser.search.searching')}
                      </Typography>
                    ) : showProductSkeletons || tabCountsLoading ? (
                      <Skeleton variant="rounded" animation="wave" width={160} height={16} />
                    ) : currentTabTotal != null && totalForPagination !== currentTabTotal ? (
                      // Filters narrowed the list: the one place this line adds a number the range label lacks.
                      <Typography sx={{ fontSize: '0.84375rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                        <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                          {numberFormat(totalForPagination)}
                        </Box>
                        {` of ${numberFormat(currentTabTotal)} ${statusScopeLabel} products`}
                      </Typography>
                    ) : (
                      // Unfiltered: the pagination bar already carries the total, so only say what it counts.
                      <Typography sx={{ fontSize: '0.84375rem', color: 'text.secondary' }}>
                        {`${statusScopeLabel.charAt(0).toUpperCase()}${statusScopeLabel.slice(1)} products`}
                      </Typography>
                    )}
                    {isStoreScoped && (
                      <Typography sx={{ fontSize: '0.71875rem', color: 'text.disabled' }}>
                        Scoped to your store brands
                        {syncSelectedIds.size > 0 && (
                          <>
                            {' · '}
                            {/* The scope is every product of your brands; this is the smaller set you actually sync. */}
                            {syncStatusFilter === 'in_sync' ? (
                              <Box component="span" sx={{ color: 'text.secondary' }}>{tx('productBrowser.inMySync.active')}</Box>
                            ) : (
                              <Link
                                component="button"
                                type="button"
                                underline="always"
                                color="inherit"
                                aria-label={tx('productBrowser.inMySync.aria')}
                                onClick={() => setSyncStatusFilter('in_sync')}
                                sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                              >
                                {tx('productBrowser.inMySync.link', { count: syncSelectedIds.size })}
                              </Link>
                            )}
                          </>
                        )}
                      </Typography>
                    )}
                  </Box>
                  {hasActiveFilters && (
                    <Button
                      size="small"
                      onClick={handleClearAllFilters}
                      sx={{ minWidth: 0, p: 0, fontSize: '0.84375rem', color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'transparent' } }}
                    >
                      Clear filters
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Status banner — widening past Active raises this instead of silently hiding archived products */}
              {catalogStatus !== 'active' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.125,
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.125,
                    bgcolor: tokens.signal.caution.tint,
                    borderTop: `1px solid ${tokens.signal.caution.border}`,
                    borderBottom: `1px solid ${tokens.signal.caution.border}`,
                  }}
                >
                  <WarningAmber sx={{ fontSize: 16, color: 'warning.main' }} />
                  <Typography sx={{ fontSize: '0.8125rem', color: tokens.signal.caution.ink }}>
                    {catalogStatus === 'archived'
                      ? 'Showing archived products only — these are hidden from the API and storefronts.'
                      : `Results include archived and discontinued products.${
                          camerasData?.pagination?.statusCounts
                            ? ` (${[
                                camerasData.pagination.statusCounts.active != null && `${numberFormat(camerasData.pagination.statusCounts.active)} active`,
                                camerasData.pagination.statusCounts.archived != null && `${numberFormat(camerasData.pagination.statusCounts.archived)} archived`,
                                camerasData.pagination.statusCounts.discontinued != null &&
                                  `${numberFormat(camerasData.pagination.statusCounts.discontinued)} discontinued`,
                              ]
                                .filter(Boolean)
                                .join(' · ')})`
                            : ''
                        }`}
                  </Typography>
                </Box>
              )}

              {productUploadEnabled && showProductUpload && (
                <Box sx={{ px: { xs: 1.25, sm: 2.5 }, py: 1.5 }}>
                  <StoreProductUploadCard selectedIds={syncSelectedIds} />
                </Box>
              )}

              {productSubmissionsEnabled && submissionScope && showProductSubmissions && (
                <Box sx={{ px: { xs: 1.25, sm: 2.5 }, py: 1.5 }}>
                  <ProductSubmissionsCard
                    scope={submissionScope}
                    selectedIds={submissionScope === 'store' ? syncSelectedIds : undefined}
                    onOpenProduct={openProductById}
                    privateUploadEnabled={productUploadFlag}
                  />
                </Box>
              )}

              {placeholdersEnabled && showPlaceholders && (
                <Box sx={{ px: { xs: 1.25, sm: 2.5 }, py: 1.5 }}>
                  <PricingOnlyPlaceholdersCard integrationId={selectionIntegrationId} selectedIds={syncSelectedIds} />
                </Box>
              )}

              {isStoreScoped && storeCountsIsError && !storeCatalogCounts && (
                <Alert severity="warning" sx={{ mx: { xs: 1.25, sm: 2.5 }, mt: 1.5, mb: 0 }}>
                  Couldn&apos;t load catalog totals. Showing a wider product list so discontinued items you still sync are not hidden.
                </Alert>
              )}

              {/* Discontinued-in-sync banner — these rows are inside the Active default
                  view because the store is still pushing them to its storefront. */}
              {showDiscontinuedInSyncBanner && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1.125,
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.125,
                    bgcolor: tokens.signal.caution.tint,
                    borderTop: `1px solid ${tokens.signal.caution.border}`,
                    borderBottom: `1px solid ${tokens.signal.caution.border}`,
                    flexWrap: 'wrap',
                  }}
                >
                  <WarningAmber sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.8125rem', color: tokens.signal.caution.ink, flex: 1, minWidth: 200 }}>
                    {discontinuedInSyncOnly
                      ? `Showing the ${numberFormat(discontinuedInSyncVisible)} discontinued ${
                          discontinuedInSyncVisible === 1 ? 'product' : 'products'
                        } you're still syncing.`
                      : `${numberFormat(discontinuedInSyncVisible)} discontinued ${
                          discontinuedInSyncVisible === 1 ? 'product is' : 'products are'
                        } included because you're still syncing ${
                          discontinuedInSyncVisible === 1 ? 'it' : 'them'
                        } to your storefront.`}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setDiscontinuedInSyncOnly((prev) => !prev)}
                    sx={{ flexShrink: 0, fontSize: '0.8125rem', color: tokens.signal.caution.ink, '&:hover': { bgcolor: 'rgba(146, 64, 14, 0.08)' } }}
                  >
                    {discontinuedInSyncOnly ? 'Back to all products' : 'View discontinued in sync items'}
                  </Button>
                </Box>
              )}

              {showProductSkeletons ? (
                viewMode === 'card' ? (
                  <ProductCardSkeletonGrid showStoreChrome={isStoreScoped} />
                ) : (
                  <ProductListSkeleton isStoreScoped={isStoreScoped} />
                )
              ) : (
                <Box
                  aria-busy={resultsRefreshing || undefined}
                  sx={{ opacity: resultsRefreshing ? 0.72 : 1, transition: 'opacity 150ms linear' }}
                >
                  {resultsRefreshing && (
                    <LinearProgress
                      aria-label={searchBusy ? tx('productBrowser.search.searching') : tx('productBrowser.search.updating')}
                    />
                  )}
                  <PaginationBar
                    limit={limit}
                    onLimitChange={setLimit}
                    rangeLabel={rangeLabel}
                    page={page}
                    pageCount={pageCount}
                    onPageChange={(value) => setPage(value)}
                    middle={selectionMiddleContent ?? nextSyncMiddleContent}
                    active={totalSelected > 0}
                    disabled={isFetching}
                  />

                  {showScopedEmptyState ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {syncStatusFilter === 'sync_issues' ? 'No sync issues match your filters' : 'No products available for your selected brands'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {syncStatusFilter === 'sync_issues'
                          ? 'No products with recorded sync errors or actionable skips match the current filters.'
                          : 'Your product catalog is scoped to your brand associations. Add or update store brand selections to see more products.'}
                      </Typography>
                    </Box>
                  ) : viewMode === 'card' ? (
                    <Box sx={PRODUCT_CARD_GRID_SX}>
                      {camerasWithPricing.map((camera) => {
                        const pricing = buildCardPricing(camera);
                        const mark = resolveBrandMark(camera.brand);
                        const inSync = syncSelectedIds.has(camera.id);
                        const photosCount = camera.images?.length ?? 0;
                        const specCount = camera.specifications
                          ? camera.specifications._specCount
                            ? parseInt(camera.specifications._specCount, 10)
                            : Object.keys(camera.specifications).length
                          : 0;
                        const changedLabel = formatRelativeDate(lastPriceChangeAt(camera)) || formatRelativeDate(camera.firstImportedAt);
                        const dataCaption = specCount > 0 || changedLabel ? `${pluralize(specCount, 'spec')}${changedLabel ? ` · ${changedLabel}` : ''}` : '';
                        const identityLine = catalogIdentityLine(camera);
                        const badges = [
                          camera.isProOnly && 'Pro only',
                          camera.parentSku && 'Kit variant',
                          !variantGroupSelectEnabled && !camera.parentSku && camera.variantGroupId && 'Has variants',
                        ].filter(Boolean) as string[];
                        const outOfStock = camera.manufacturerStock?.status === 'out_of_stock';
                        // Lifecycle chip (flag `preorder_lifecycle`) names the stage; the B17 pre-order chip stands in when it is off.
                        const lifecycleStage = lifecycleEnabled ? lifecycleStageOf(camera) : 'released';
                        const lifecycleChip = lifecycleStage !== 'released' ? lifecycleStage : null;
                        const preorder = !lifecycleChip && preorderChipEnabled && isProductInPreorder(camera);
                        const selected = selectedProductIds.has(camera.id);
                        const priceUnverified =
                          syncPriceVerificationAlertsEnabled &&
                          isStoreScoped &&
                          isPriceUnverifiedInSync(camera, syncSelectedIds);

                        return (
                          <Paper
                            key={camera.id}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              height: '100%',
                              overflow: 'hidden',
                              ...(selected ? { borderColor: 'primary.light' } : {}),
                            }}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                                height: 150,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.50',
                                borderBottom: '1px solid',
                                borderColor: 'grey.100',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                component="img"
                                loading="lazy"
                                src={toAbsoluteImageUrl(camera.images?.[0]) ?? getBrandLogo(camera.brand)}
                                alt={camera.name || camera.model}
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).src = getBrandLogo(camera.brand);
                                }}
                                onClick={() => handleImageClick(camera, 0)}
                                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'pointer', opacity: camera.isArchived ? 0.6 : 1, p: 1.5 }}
                              />
                              {isStoreScoped && (
                                <Checkbox
                                  size="small"
                                  checked={selected}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => handleToggleProductSelection(camera.id)}
                                  sx={{ position: 'absolute', top: 6, left: 6, p: 0.375, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}
                                />
                              )}
                              {isStoreScoped && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.625,
                                    height: 22,
                                    px: 1,
                                    borderRadius: '11px',
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: inSync ? 'success.main' : 'grey.300' }} />
                                  <Typography sx={{ fontSize: '0.71875rem', fontWeight: 600, color: inSync ? 'success.dark' : 'text.secondary' }}>
                                    {inSync ? 'In sync' : 'Not in sync'}
                                  </Typography>
                                </Box>
                              )}
                              {photosCount > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: 20,
                                    px: 0.875,
                                    borderRadius: '4px',
                                    bgcolor: 'rgba(15,23,42,0.62)',
                                    color: 'common.white',
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {photosCount} photo{photosCount === 1 ? '' : 's'}
                                </Box>
                              )}
                              {camera.isArchived && (
                                <Chip
                                  icon={<Archive sx={{ fontSize: 13 }} />}
                                  label="Archived"
                                  size="small"
                                  color="warning"
                                  sx={{ position: 'absolute', top: 8, left: isStoreScoped ? 36 : 8, height: 20, fontSize: '0.625rem' }}
                                />
                              )}
                              {priceUnverified && (
                                <Tooltip
                                  title={
                                    (camera.pricing?.verification?.unverifiedSince
                                      ? `${t('pricing.priceUnverified.since')} ${formatDate(camera.pricing.verification.unverifiedSince)}. `
                                      : '') +
                                    (camera.pricing?.verification?.lastVerifiedPrice != null
                                      ? `${t('pricing.priceUnverified.lastVerified')} ${formatCurrency(camera.pricing.verification.lastVerifiedPrice)}.`
                                      : '')
                                  }
                                >
                                  {/* StatusChip is not forwardRef; Box carries Tooltip listeners. */}
                                  <Box
                                    component="span"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      left: isStoreScoped ? (camera.isArchived ? 118 : 36) : (camera.isArchived ? 82 : 8),
                                    }}
                                  >
                                    <StatusChip
                                      label={t('pricing.priceUnverified.label')}
                                      tone="caution"
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.625rem' }}
                                    />
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>

                            <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 1.75, p: 1.75 }}>
                              {/* Identity */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <BrandMark mark={mark} size={40} fontSize={16} wide />
                                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                                    {camera.brand}
                                  </Typography>
                                  {syncIssuesEnabled && syncIssueIds.has(camera.id) && (
                                    <Tooltip title={syncIssueReasons.get(camera.id) || 'Sync issue'}>
                                      <Box component="span"><StatusChip label="Sync issue" tone="caution" size="small" /></Box>
                                    </Tooltip>
                                  )}
                                </Box>
                                <Typography
                                  onClick={() => handleCameraClick(camera)}
                                  sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.45, color: 'text.primary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                >
                                  {camera.name || camera.model}
                                </Typography>
                                {identityLine && (
                                  <Typography
                                    title={identityLine}
                                    noWrap
                                    sx={{ fontSize: '0.75rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.01em' }}
                                  >
                                    {identityLine}
                                  </Typography>
                                )}
                              </Box>

                              {/* Price */}
                              {pricing.hasPrice && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.875, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.01em', color: pricing.netIsRebate ? 'success.dark' : 'text.primary' }}>
                                      {pricing.netLabel}
                                    </Typography>
                                    {pricing.retailLabel && (
                                      <Typography sx={{ fontSize: '0.78125rem', color: 'text.disabled', textDecoration: 'line-through' }}>{pricing.retailLabel}</Typography>
                                    )}
                                    {pricing.netNote && (
                                      <Typography sx={{ fontSize: '0.71875rem', fontWeight: 600, color: 'success.dark' }}>{pricing.netNote}</Typography>
                                    )}
                                    <PendingReviewChip pricing={camera.pricing} sx={{ height: 18 }} />
                                  </Box>
                                  {pricing.offerLines.map((offer, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap', fontSize: '0.71875rem', lineHeight: 1.5 }}>
                                      {offer.upcoming && (
                                        <StatusChip
                                          label="Upcoming"
                                          tone="caution"
                                          sx={{ height: 18, alignSelf: 'center', '& .MuiChip-label': { px: 0.75, fontSize: 10, fontWeight: 600 } }}
                                        />
                                      )}
                                      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                        {offer.amount}
                                      </Box>
                                      <Box component="span" sx={{ color: 'text.secondary' }}>
                                        {offer.label}
                                      </Box>
                                      {offer.ends && (
                                        <Box component="span" sx={{ fontWeight: offer.endsAmber ? 600 : 400, color: offer.endsAmber ? tokens.signal.caution.ink : 'text.disabled' }}>
                                          · {offer.ends}
                                        </Box>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}

                              {/* Combo/sold-with rebates: shown, but never folded
                                  into the net price above — they need a second
                                  item. Renders null when there are none, and is
                                  outside the price gate so an unpriced product
                                  still surfaces the offer. */}
                              <ComboRebateOffersNote offers={camera.pricingData?.comboOffers} compact />

                              {/* Badges flow with the content; only the footer row below is pinned to the card bottom. */}
                                {(pricing.mapMissing || pricing.mapLine || badges.length > 0 || (variantGroupSelectEnabled && !!camera.variantGroupId) || outOfStock || preorder || hasMultipleStoreLocations) && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    {pricing.mapMissing ? (
                                      <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: 0.875, borderRadius: '4px', bgcolor: tokens.signal.caution.tint, border: `1px solid ${tokens.signal.caution.border}`, color: tokens.signal.caution.ink, fontSize: '0.6875rem', fontWeight: 600 }}>
                                        No MAP
                                      </Box>
                                    ) : pricing.mapLine ? (
                                      <Typography sx={{ fontSize: '0.71875rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{pricing.mapLine}</Typography>
                                    ) : null}
                                    {badges.map((badge) => (
                                      <Box key={badge} sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: 0.875, borderRadius: '4px', bgcolor: 'grey.100', color: 'text.secondary', fontSize: '0.6875rem', fontWeight: 600 }}>
                                        {badge}
                                      </Box>
                                    ))}
                                    {variantGroupSelectEnabled && camera.variantGroupId && (
                                      <VariantGroupSelectAll
                                        variant="chip"
                                        productId={camera.id}
                                        variantGroupId={camera.variantGroupId}
                                        integrationId={selectionIntegrationId}
                                        selectedIds={syncSelectedIds}
                                        onCapError={setSyncCapError}
                                      />
                                    )}
                                    {outOfStock && (
                                      <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: 0.875, borderRadius: '4px', bgcolor: tokens.signal.problem.tint, color: 'error.main', fontSize: '0.6875rem', fontWeight: 600 }}>
                                        Out of stock
                                      </Box>
                                    )}
                                    {lifecycleChip && <ProductLifecycleChip lifecycle={camera.lifecycle} />}
                                    {preorder && (
                                      <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: 0.875, borderRadius: '4px', bgcolor: tokens.signal.caution.tint, border: `1px solid ${tokens.signal.caution.border}`, color: tokens.signal.caution.ink, fontSize: '0.6875rem', fontWeight: 600 }}>
                                        {t('productPreorder.chip')}
                                      </Box>
                                    )}
                                    {hasMultipleStoreLocations && pricing.hasPrice && (
                                      <VariesByLocationChip
                                        productId={camera.id}
                                        enabled={hasMultipleStoreLocations}
                                        label={t('pricing.priceByLocation.varies')}
                                      />
                                    )}
                                    {isStoreScoped && <TradeInCreditLine productId={camera.id} compact />}
                                  </Box>
                                )}
                              {/* Footer — margin-top auto pins it to the bottom edge, so a card whose title or
                                  meta wraps still lines up with its neighbours in the row. */}
                              <Box sx={{ mt: 'auto', pt: 1.375, borderTop: '1px solid', borderColor: 'grey.100' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                  <Typography sx={{ fontSize: '0.71875rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>{dataCaption}</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Button size="small" variant="outlined" onClick={() => handleCameraClick(camera)} sx={{ height: 26, px: 1.25, fontSize: '0.71875rem' }}>
                                      Details
                                    </Button>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ bgcolor: 'grey.50' }}>
                      <Box
                        sx={{
                          display: isNarrow ? 'none' : 'grid',
                          gridTemplateColumns: listGridColumns,
                          alignItems: 'center',
                          gap: 1.5,
                          px: { xs: 1.5, sm: 2.5 },
                          py: 1.125,
                          bgcolor: 'grey.50',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                        }}
                      >
                        {isStoreScoped && <Box />}
                        <Box />
                        <Box>Product</Box>
                        <Box>Category</Box>
                        <Box sx={{ textAlign: 'right' }}>Price</Box>
                        <Box>Flags</Box>
                        <Box sx={{ textAlign: 'right' }}>Data</Box>
                      </Box>
                      {camerasWithPricing.map((camera) => {
                        const pricing = buildCardPricing(camera);
                        const inSync = syncSelectedIds.has(camera.id);
                        const selected = selectedProductIds.has(camera.id);
                        const listOfferBits = pricing.offerLines
                          .map((o) => `${o.upcoming ? 'Upcoming ' : ''}${o.amount} ${o.label === 'instant rebate' ? 'IR' : o.label === 'dealer reimbursement' ? 'reimb' : 'off'}`)
                          .join(' · ');
                        const specCount = camera.specifications
                          ? camera.specifications._specCount
                            ? parseInt(camera.specifications._specCount, 10)
                            : Object.keys(camera.specifications).length
                          : 0;
                        const changedLabel = formatRelativeDate(lastPriceChangeAt(camera)) || formatRelativeDate(camera.firstImportedAt);
                        const dataCaption = specCount > 0 || changedLabel ? `${pluralize(specCount, 'spec')}${changedLabel ? ` · ${changedLabel}` : ''}` : '';
                        const identityLine = catalogIdentityLine(camera);

                        return (
                          <Box
                            key={camera.id}
                            onClick={() => handleCameraClick(camera)}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: listGridColumns,
                              alignItems: 'center',
                              gap: { xs: 1, sm: 1.5 },
                              px: { xs: 1.5, sm: 2.5 },
                              py: 1,
                              minHeight: 54,
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              bgcolor: selected ? (theme: Theme) => alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: selected ? (theme: Theme) => alpha(theme.palette.primary.main, 0.08) : 'grey.50' },
                            }}
                          >
                            {isStoreScoped && (
                              <Checkbox
                                size="small"
                                checked={selected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => handleToggleProductSelection(camera.id)}
                                sx={{ p: 0.25 }}
                              />
                            )}
                            <Box sx={{ width: 44, height: 38, borderRadius: '6px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                              <Box
                                component="img"
                                loading="lazy"
                                src={toAbsoluteImageUrl(camera.images?.[0]) ?? getBrandLogo(camera.brand)}
                                alt=""
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).src = getBrandLogo(camera.brand);
                                }}
                                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <Typography noWrap sx={{ fontSize: '0.84375rem', fontWeight: 600, color: 'text.primary' }}>
                                {camera.name || camera.model}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, fontSize: '0.75rem', color: 'text.disabled', minWidth: 0 }}>
                                <span>{camera.brand}</span>
                                {identityLine && (
                                  <>
                                    <span>·</span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{identityLine}</span>
                                  </>
                                )}
                              </Box>
                              {isNarrow && (
                                <Typography sx={{ fontSize: '0.84375rem', fontWeight: 600, color: pricing.netIsRebate ? 'success.dark' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                  {pricing.netLabel}
                                  {pricing.retailLabel ? (
                                    <Box component="span" sx={{ ml: 0.75, fontSize: '0.71875rem', fontWeight: 400, color: 'text.disabled', textDecoration: 'line-through' }}>
                                      {pricing.retailLabel}
                                    </Box>
                                  ) : null}
                                </Typography>
                              )}
                            </Box>
                            <Typography noWrap sx={{ display: isNarrow ? 'none' : 'block', fontSize: '0.78125rem', color: 'text.secondary' }}>
                              {camera.category}
                            </Typography>
                            <Box sx={{ display: isNarrow ? 'none' : 'flex', textAlign: 'right', flexDirection: 'column', gap: 0.125, alignItems: 'flex-end' }}>
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                <Typography sx={{ fontSize: '0.84375rem', fontWeight: 600, color: pricing.netIsRebate ? 'success.dark' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                  {pricing.netLabel}
                                </Typography>
                                {pricing.retailLabel && (
                                  <Typography sx={{ fontSize: '0.71875rem', color: 'text.disabled', textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>
                                    {pricing.retailLabel}
                                  </Typography>
                                )}
                              </Box>
                              {listOfferBits && (
                                <Typography sx={{ fontSize: '0.71875rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{listOfferBits}</Typography>
                              )}
                            </Box>
                            <Box sx={{ display: isNarrow ? 'none' : 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              {isStoreScoped && (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', color: inSync ? 'success.dark' : 'text.secondary' }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: inSync ? 'success.main' : 'grey.300' }} />
                                  {inSync ? 'In sync' : 'Not in sync'}
                                </Box>
                              )}
                              {lifecycleEnabled && <ProductLifecycleChip lifecycle={camera.lifecycle} />}
                              {syncIssuesEnabled && syncIssueIds.has(camera.id) && (
                                <Tooltip title={syncIssueReasons.get(camera.id) || 'Sync issue'}>
                                  <Box component="span"><StatusChip label="Sync issue" tone="caution" size="small" /></Box>
                                </Tooltip>
                              )}
                              {pricing.mapMissing && (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: 0.75, borderRadius: '4px', bgcolor: tokens.signal.caution.tint, border: `1px solid ${tokens.signal.caution.border}`, color: tokens.signal.caution.ink, fontSize: '0.6875rem', fontWeight: 600 }}>
                                  No MAP
                                </Box>
                              )}
                              {pricing.hasComboOffers && (
                                <Box
                                  title="Combo rebate available — does not change this item's price"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: 20,
                                    px: 0.75,
                                    borderRadius: '4px',
                                    bgcolor: 'action.hover',
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Combo IR
                                </Box>
                              )}
                              {pricing.rebateUpcoming && (
                                <StatusChip
                                  label="Upcoming"
                                  tone="caution"
                                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11, fontWeight: 600 } }}
                                />
                              )}
                              {pricing.rebateEndsLabel && (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: 20,
                                    px: 0.75,
                                    borderRadius: '4px',
                                    bgcolor: pricing.rebateUpcoming || pricing.rebateEndsAmber ? tokens.signal.caution.tint : tokens.signal.positive.tint,
                                    border: '1px solid',
                                    borderColor: pricing.rebateUpcoming || pricing.rebateEndsAmber ? tokens.signal.caution.border : tokens.signal.positive.border,
                                    color: pricing.rebateUpcoming || pricing.rebateEndsAmber ? tokens.signal.caution.ink : 'success.dark',
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {pricing.rebateEndsLabel.charAt(0).toUpperCase() + pricing.rebateEndsLabel.slice(1)}
                                </Box>
                              )}
                              {hasMultipleStoreLocations && pricing.hasPrice && (
                                <VariesByLocationChip
                                  productId={camera.id}
                                  enabled={hasMultipleStoreLocations}
                                  label={t('pricing.priceByLocation.varies')}
                                />
                              )}
                              {isStoreScoped && <TradeInCreditLine productId={camera.id} compact />}
                            </Box>
                            <Typography sx={{ display: isNarrow ? 'none' : 'block', textAlign: 'right', fontSize: '0.75rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>{dataCaption}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  <PaginationBar
                    limit={limit}
                    onLimitChange={setLimit}
                    rangeLabel={rangeLabel}
                    page={page}
                    pageCount={pageCount}
                    onPageChange={(value) => setPage(value)}
                    middle={selectionMiddleContent}
                    active={totalSelected > 0}
                    disabled={isFetching}
                  />
                </Box>
              )}
            </Paper>

        {/* Product Details Dialog */}
        <ProductDetailsDialog
          open={permalinksOn ? !!dialogProduct : dialogOpen}
          product={dialogProduct}
          onClose={handleCloseDialog}
          onVariantClick={handleVariantOptionClick}
          onLinkedProductClick={openLinkedCatalogProduct}
          variantSwitchLoadingSku={variantSwitchLoadingSku}
          onOpenGallery={handleOpenGalleryFromDialog}
          onFeedback={(feedback) => showToast(feedback.message, feedback.severity)}
          brandMark={resolveBrandMark(dialogProduct?.brand)}
          syncSelectedIds={syncSelectedIds}
          selectionIntegrationId={selectionIntegrationId}
          isStoreScoped={isStoreScoped}
          isPlatformAdmin={isPlatformAdmin}
          isVendor={isVendor}
          canManageStoreOverrides={canManageStoreOverrides}
          canManageStoreAiCopy={canManageStoreAiCopy}
          showStoreOverridesPanel={showStoreOverridesPanel}
          allowBelowMap={!!pricingPreferences?.allowBelowMap}
          onAddToSync={() => dialogProduct && addToSyncMutation.mutate([dialogProduct.id])}
          onAddProductsToSync={(ids) => addRelatedToSyncMutation.mutateAsync(ids)}
          addToSyncPending={addToSyncMutation.isPending}
          onRemoveFromSync={() => dialogProduct && removeFromSyncMutation.mutate([dialogProduct.id])}
          removeFromSyncPending={removeFromSyncMutation.isPending}
          onViewSyncSelection={() => {
            handleCloseDialog();
            setSyncStatusFilter('in_sync');
          }}
        />

        {/* Image Gallery */}
        <ImageGallery
          images={galleryImages}
          title={galleryTitle}
          open={imageGalleryOpen}
          onClose={handleCloseImageGallery}
          initialIndex={galleryInitialIndex}
        />
        <Portal>
        </Portal>
      </Box>
    </>
  );
};
